import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import api, { formatApiError, detailFromAxiosError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  ShieldCheck, Lock, Loader2, UserCog, AlertTriangle, 
  UserPlus, Trash2, Mail, User, ServerCog, Copy, FileDown, Upload,
  CheckCircle2, XCircle, Pencil, Building2, ArchiveRestore
} from "lucide-react";
import { toast } from "sonner";
import { useWorkspaceMode } from "@/lib/workspaceMode";
import { useSearchParams, Link } from "react-router-dom";
import { formatLicenseDateForDisplay } from "@/lib/licenseDates";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { resolveModuleCatalog, defaultEntitlements } from "@/modules/catalogModulos";
import ActiveLicenseModulesList from "@/components/ActiveLicenseModulesList";
import LicenseActivationPanel from "@/components/LicenseActivationPanel";
import LicenseCommercialSection from "@/components/LicenseCommercialSection";
import LicenseRenewalExpansionPanel from "@/components/LicenseRenewalExpansionPanel";
import { showLicenseActivationPanel } from "@/lib/licenseRenewal";
import { LICENSE_INSTALL_NOTICE_TITLE, LICENSE_INSTALL_NOTICE_PARAS } from "@/lib/licenseInstallNotice";
import {
  canShowCommercialLicenseRequestUi,
  hasActiveCommercialLicense,
} from "@/lib/commercialLicenseRequests";
import {
  LICENSE_RENEWAL_EXPANSION_QUERY,
  LICENSE_REQUEST_HISTORY_QUERY,
  mergeLicenciaDeepLinkOpenSections,
  parseRenovacionAmpliacionPrefill,
} from "@/modules/config/configuracionRoutes";
import {
  shouldHideBuyerTechnicalSatDiagnose as resolveHideBuyerTechnicalSatDiagnose,
  shouldHideBuyerConfigAdminTabs as resolveHideBuyerConfigAdminTabs,
  shouldHideBuyerTechnicalUi as resolveHideBuyerTechnicalUi,
  shouldSkipBuyerSecurityKeys,
} from "@/lib/buyerConfigUi";
import SecurityRecoveryQuestionFields from "@/components/SecurityRecoveryQuestionFields";
import DesignerUiAccessConfig from "@/components/DesignerUiAccessConfig";
import SatUiDiagnoseSettings from "@/components/sat/SatUiDiagnoseSettings";
import { getStoredWorkModule, VISOR_MODULE_BREADCRUMB_ROOT, WORK_MODULES } from "@/modules/workModuleCore";
import { invalidateEntitlementsCache } from "@/modules/useEntitlements";
import { useDesignerUiAccess } from "@/lib/designerUiAccess";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { usePlatformDesignerBuyerPreview } from "@/lib/usePlatformDesignerBuyerPreview";
import { blocksBuyerCommercialLicenseUi } from "@/lib/licenseCommercialFocusMode";
import { canManageWorkspaceTeam } from "@/lib/auxiliaryRoleGuide";
import { WORKSPACE_MODE_PLATFORM } from "@/lib/workspaceMode";
import UserRoleField from "@/components/users/UserRoleField";
import UserManagementGroupedTables from "@/components/users/UserManagementGroupedTables";
import BuyerRequestsDesignerPanel from "@/components/users/BuyerRequestsDesignerPanel";
import PlatformLicenseRequests from "@/pages/platform/PlatformLicenseRequests";
import {
  getAssignableWorkspaceRoleOptions,
  getWorkspaceRoleBadgeClass,
  getWorkspaceRoleLabel,
} from "@/lib/userRoles";
import { canInstallLicenseForWorkspace, shouldSuppressLicenseCtasForPlatformDesigner } from "@/lib/accountStatus";
import { shouldUseLicenseCommercialFocusView } from "@/lib/licenseCommercialFocusMode";
import LicenseCommercialFocusView from "@/pages/LicenseCommercialFocusView";
import BuyerMiNegocioView from "@/components/BuyerMiNegocioView";
import { formatRegimenSatSelectLabel, REGIMENES_SAT } from "@/lib/regimenesSat";

const DEFAULT_SECURITY_ROWS = () => [
  { q: "", answer: "" },
  { q: "", answer: "" },
  { q: "", answer: "" },
];

function securityRowsFromApi(sr) {
  if (!sr?.configured || !Array.isArray(sr.questions) || sr.questions.length !== 3) {
    return DEFAULT_SECURITY_ROWS();
  }
  const ans = Array.isArray(sr.answers) ? sr.answers : [];
  return sr.questions.map((item, i) => ({
    q: String(item.q ?? ""),
    answer: String(ans[i] ?? ""),
  }));
}

function EmisorScopeNotice() {
  return (
    <div
      className="rounded-md border border-violet-200 bg-violet-50/90 px-4 py-4 text-sm text-slate-800 space-y-3"
      data-testid="config-emisor-scope-notice"
      role="note"
    >
      <p className="m-0 leading-relaxed">
        Esta pantalla solo concentra la emisión de licencias distribuidor. Usuarios BNT, herramientas técnicas y
        configuración del negocio se administran fuera del gestor.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm" className="h-8 text-xs border-violet-300 bg-white">
          <Link to="/usuarios-distribuidor">Usuarios Administradores BNT</Link>
        </Button>
        <Button asChild variant="outline" size="sm" className="h-8 text-xs border-violet-300 bg-white">
          <Link to="/disenador/herramientas">Herramientas Técnicas</Link>
        </Button>
        <Button asChild variant="outline" size="sm" className="h-8 text-xs border-violet-300 bg-white">
          <Link to="/cartera-licencias">Cartera de licencias</Link>
        </Button>
        <Button asChild variant="outline" size="sm" className="h-8 text-xs border-violet-300 bg-white">
          <Link to="/catalogo-compradores">Catálogo de compradores</Link>
        </Button>
        <Button asChild variant="outline" size="sm" className="h-8 text-xs border-violet-300 bg-white">
          <Link to="/despacho/configuracion">Configuración del despacho</Link>
        </Button>
      </div>
    </div>
  );
}

function SystemTab({
  isOwner,
  canInstallLicense,
  showSolicitudLicenciaBanner,
  showLicenseSigningHints,
  showActivarLicenciaScroll,
  showBuyerPanelHint,
  emisorView = false,
}) {
  const { user, refreshUser } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  const buyerPreviewActive = usePlatformDesignerBuyerPreview();
  const blockBuyerCommercialUi = blocksBuyerCommercialLicenseUi(user, {
    buyerPreviewActive,
    isPlatformDesigner,
  });
  const { mode, setMode, canSwitch } = useWorkspaceMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const renewalDeepLink = useMemo(() => parseRenovacionAmpliacionPrefill(searchParams), [searchParams]);
  const clearRenewalDeepLink = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(LICENSE_RENEWAL_EXPANSION_QUERY);
    next.delete(LICENSE_REQUEST_HISTORY_QUERY);
    next.delete("expansion_type");
    next.delete("expansion_module");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [prof, setProf] = useState({ 
    razon_social: "", rfc: "", email: "", correo_facturacion: "", nombre_comercial: "", 
    logo_base64: "", domicilio: "", responsable: "", telefono: "",
    regimen_fiscal: "", codigo_postal: ""
  });
  const [busyProf, setBusyProf] = useState(false);
  const [secRows, setSecRows] = useState(DEFAULT_SECURITY_ROWS);
  const [licToken, setLicToken] = useState("");
  const [busyLic, setBusyLic] = useState(false);
  
  const [gen, setGen] = useState({ 
    licensee_name: "", licensee_rfc: "", licensee_email: "", installation_id: "",
    valid_from: new Date().toISOString().slice(0,10), valid_to: "", plan: "standard",
    contract_equipment_total: "",
    contract_equipment_slot: "",
    visor_sat_permiso: true,
    laboral_permiso: false,
    entitlements: defaultEntitlements(),
  });
  const moduleCatalog = useMemo(() => resolveModuleCatalog(info), [info]);

  const setEntitlement = (moduleId, value) => {
    setGen((prev) => {
      const ent = { ...(prev.entitlements || defaultEntitlements()), [moduleId]: !!value };
      return {
        ...prev,
        entitlements: ent,
        visor_sat_permiso: Boolean(ent.visor),
        laboral_permiso: Boolean(ent.laboral),
      };
    });
  };
  const [genToken, setGenToken] = useState("");
  const [genSaveRegistry, setGenSaveRegistry] = useState(true);
  const [genRegistryPay, setGenRegistryPay] = useState("pendiente");
  const [genRegistryNotes, setGenRegistryNotes] = useState("");
  const [genPortalUsername, setGenPortalUsername] = useState("");
  const [genPortalPassword, setGenPortalPassword] = useState("");
  /** Credenciales usadas en la última emisión (para re-descargar el PDF sin reescribir el formulario). */
  const [lastEmitPortal, setLastEmitPortal] = useState({ username: "", password: "" });
  const [busySolicPdf, setBusySolicPdf] = useState(false);
  const [busyActivacionPdf, setBusyActivacionPdf] = useState(false);
  const [busyGenerate, setBusyGenerate] = useState(false);
  const [genAutoActivacionPdf, setGenAutoActivacionPdf] = useState(true);
  const [busyImportPdf, setBusyImportPdf] = useState(false);
  const genEmitSolicitudPdfRef = useRef(null);
  const [busyGenSolicitudAttach, setBusyGenSolicitudAttach] = useState(false);
  const [licenseInstallNoticeOpen, setLicenseInstallNoticeOpen] = useState(false);
  const [designerCurPwd, setDesignerCurPwd] = useState("");
  const [designerNewPwd, setDesignerNewPwd] = useState("");
  const [designerConfirmPwd, setDesignerConfirmPwd] = useState("");
  const [busyDesignerPwd, setBusyDesignerPwd] = useState(false);
  const [securityKeys, setSecurityKeys] = useState(null);
  const [securityKeysError, setSecurityKeysError] = useState("");

  const changeDesignerPassword = async () => {
    if (designerNewPwd.length < 4) {
      toast.error("La nueva contraseña debe tener al menos 4 caracteres");
      return;
    }
    if (designerNewPwd !== designerConfirmPwd) {
      toast.error("Las contraseñas nuevas no coinciden");
      return;
    }
    setBusyDesignerPwd(true);
    try {
      await api.put("/auth/designer-backdoor/password", {
        current_password: designerCurPwd,
        new_password: designerNewPwd,
      });
      toast.success("Contraseña de diseñador actualizada");
      setDesignerCurPwd("");
      setDesignerNewPwd("");
      setDesignerConfirmPwd("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cambiar la contraseña");
    } finally {
      setBusyDesignerPwd(false);
    }
  };

  /** Descarga confiable del PDF y valida cabecera %PDF (evita «éxito» silencioso con JSON/HTML en blob). */
  const saveActivacionPdfBlob = async (blob, successMessage) => {
    if (!(blob instanceof Blob)) {
      toast.error("Respuesta inválida del servidor");
      return false;
    }
    if (blob.size === 0) {
      toast.error("El servidor devolvió un archivo vacío");
      return false;
    }
    if (blob.size < 4096) {
      const head1 = await blob.slice(0, 1).text();
      if (head1 === "{" || blob.type?.includes("json")) {
        try {
          const text = await blob.text();
          const j = JSON.parse(text);
          toast.error(formatApiError(j.detail) || "No se pudo generar el PDF");
        } catch {
          toast.error("No se pudo generar el PDF");
        }
        return false;
      }
    }
    const header = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
    const isPdf = header[0] === 0x25 && header[1] === 0x50 && header[2] === 0x44 && header[3] === 0x46;
    if (!isPdf) {
      try {
        const text = await blob.text();
        const j = JSON.parse(text);
        toast.error(formatApiError(j.detail) || "El servidor no devolvió un PDF");
      } catch {
        toast.error("El servidor no devolvió un PDF válido");
      }
      return false;
    }
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = "visor-fiscal-clave-activacion.pdf";
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 2500);
    if (successMessage) toast.success(successMessage);
    return true;
  };

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get("/system/info", { timeout: 40000 });
      setInfo(data);
      if (data.profile) {
        setProf({
          razon_social: data.profile.razon_social || "",
          rfc: data.profile.rfc || "",
          email: data.profile.email || "",
          correo_facturacion: data.profile.correo_facturacion || "",
          nombre_comercial: data.profile.nombre_comercial || "",
          logo_base64: data.profile.logo_base64 || "",
          domicilio: data.profile.domicilio || "",
          responsable: data.profile.responsable || "",
          telefono: data.profile.telefono || "",
          regimen_fiscal: data.profile.regimen_fiscal || "",
          codigo_postal: data.profile.codigo_postal || ""
        });
      }
      setSecRows(securityRowsFromApi(data.security_recovery));
      const skipSecurityKeys = shouldSkipBuyerSecurityKeys({
        user,
        workspaceMode: mode,
        canSwitch,
        buyerPreviewUx: buyerPreviewActive,
        blockBuyerCommercialUi,
        emisorView,
        canGenerateLicenses: Boolean(user?.can_generate_licenses),
      });
      if ((isOwner || user?.role === "buyer_admin") && !skipSecurityKeys) {
        try {
          const { data: sk } = await api.get("/system/security-keys", { timeout: 15000 });
          setSecurityKeys(sk);
          setSecurityKeysError("");
        } catch (skErr) {
          setSecurityKeys(null);
          setSecurityKeysError(formatApiError(skErr.response?.data?.detail) || "No se pudo cargar el estado de llaves.");
        }
      }
    } catch (e) {
      console.error("Error loading system info:", e);
      const aborted = e?.code === "ECONNABORTED" || e?.message?.includes?.("timeout");
      const net = e?.message === "Network Error" || !e?.response;
      const msg = aborted
        ? "El servidor no respondió a tiempo (40 s). Comprueba que el backend y MongoDB estén en marcha y reintenta."
        : formatApiError(e.response?.data?.detail) ||
          (net
            ? "No hay conexión con el API (/api). Inicie el backend en el puerto configurado y revise el proxy de CRA si usa yarn start."
            : "No se pudo cargar la información del sistema.");
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [mode]);

  const solicitudLicRef = useRef(null);
  const activarLicRef = useRef(null);
  const solicitudPdfInputRef = useRef(null);
  const activacionLicenciaPdfInputRef = useRef(null);
  const [busyImportActivacionLicenciaPdf, setBusyImportActivacionLicenciaPdf] = useState(false);
  useEffect(() => {
    if (!showSolicitudLicenciaBanner || loading || !info) return;
    const t = window.setTimeout(() => {
      solicitudLicRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => window.clearTimeout(t);
  }, [showSolicitudLicenciaBanner, loading, info]);

  useEffect(() => {
    if (!showActivarLicenciaScroll || loading || !info) return;
    const t = window.setTimeout(() => {
      activarLicRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    return () => window.clearTimeout(t);
  }, [showActivarLicenciaScroll, loading, info]);

  const saveProfile = async () => {
    const isSecurityEmpty = secRows.every(
      (r) => !String(r.q || "").trim() && !String(r.answer || "").trim()
    );
    const includeSecurity = mode === "despacho";
    if (includeSecurity && !isSecurityEmpty) {
      const questions = secRows.map((r) => String(r.q || "").trim());
      const unique = new Set(questions.filter(Boolean));
      if (unique.size !== questions.filter(Boolean).length) {
        toast.error("Seleccione 3 preguntas distintas de recuperación.");
        return;
      }
      for (let i = 0; i < secRows.length; i++) {
        const r = secRows[i];
        const q = String(r.q || "").trim();
        const a = String(r.answer || "").trim();
        if (!q || !a) {
          toast.error(
            `Complete la pregunta ${i + 1} y su respuesta, o deje vacío todo el bloque de recuperación`
          );
          return;
        }
      }
    }

    const payload = { ...prof };
    if (includeSecurity && !isSecurityEmpty) {
      payload.security_questions = secRows.map((r) => ({
        q: String(r.q || "").trim(),
      }));
      payload.security_answers = secRows.map((r) => String(r.answer || "").trim());
    }

    setBusyProf(true);
    try {
      await api.put("/system/profile", payload);
      toast.success("Datos del titular actualizados");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally {
      setBusyProf(false);
    }
  };

  const clearSecurityRecovery = async () => {
    if (
      !window.confirm(
        "¿Eliminar las preguntas personalizadas? En «Olvidé mi contraseña» volverán las preguntas de demostración hasta que configure de nuevo."
      )
    ) {
      return;
    }
    setBusyProf(true);
    try {
      await api.put("/system/profile", { ...prof, clear_security_recovery: true });
      toast.success("Preguntas de recuperación restablecidas");
      setSecRows(DEFAULT_SECURITY_ROWS());
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally {
      setBusyProf(false);
    }
  };

  const installLicense = async () => {
    if (!licToken.trim()) { toast.error("Pega la clave de licencia"); return; }
    setBusyLic(true);
    try {
      const { data } = await api.put("/system/license", { license_token: licToken.trim() });
      toast.success("Licencia instalada correctamente");
      if (data?.post_install_data_notice) setLicenseInstallNoticeOpen(true);
      setLicToken("");
      invalidateEntitlementsCache();
      await load();
      if (typeof refreshUser === "function") await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Clave inválida");
    } finally {
      setBusyLic(false);
    }
  };

  const onImportActivacionLicenciaPdf = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Selecciona un archivo PDF");
      return;
    }
    setBusyImportActivacionLicenciaPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/system/license/import-activacion-pdf", fd);
      toast.success("Licencia activada desde el PDF");
      if (data?.post_install_data_notice) setLicenseInstallNoticeOpen(true);
      invalidateEntitlementsCache();
      await load();
      if (typeof refreshUser === "function") await refreshUser();
    } catch (err) {
      toast.error(
        (await detailFromAxiosError(err)) ||
          formatApiError(err.response?.data?.detail) ||
          "No se pudo leer o validar el PDF de activación",
      );
    } finally {
      setBusyImportActivacionLicenciaPdf(false);
    }
  };

  const generateLicense = async () => {
    if (!gen.licensee_name || !gen.licensee_rfc || !gen.valid_to || !gen.installation_id) {
      toast.error("Completa todos los campos, incluyendo el ID de Instalación");
      return;
    }
    if (!gen.valid_from || !String(gen.valid_from).trim()) {
      toast.error("Indica la fecha «Vigencia desde»");
      return;
    }
    const ctRaw = String(gen.contract_equipment_total ?? "").trim();
    const csRaw = String(gen.contract_equipment_slot ?? "").trim();
    if (csRaw && !ctRaw) {
      toast.error("Indica cuántos equipos tiene el contrato si especificas el número de equipo de esta licencia");
      return;
    }
    if (ctRaw && csRaw) {
      const ct = parseInt(ctRaw, 10);
      const cs = parseInt(csRaw, 10);
      if (!Number.isFinite(ct) || !Number.isFinite(cs) || ct < 1 || cs < 1) {
        toast.error("Equipos contratados y número de equipo deben ser enteros válidos");
        return;
      }
      if (cs > ct) {
        toast.error("El número de equipo no puede ser mayor que los equipos contratados");
        return;
      }
    }
    const pu = (genPortalUsername || "").trim().toLowerCase();
    const pp = (genPortalPassword || "").trim();
    if (pu && pp.length > 0 && pp.length < 6) {
      toast.error("La contraseña del portal debe tener al menos 6 caracteres");
      return;
    }
    setBusyGenerate(true);
    try {
      const body = {
        licensee_name: gen.licensee_name,
        licensee_rfc: gen.licensee_rfc,
        licensee_email: gen.licensee_email,
        installation_id: gen.installation_id,
        valid_from: gen.valid_from,
        valid_to: gen.valid_to,
        plan: gen.plan,
        visor_sat_permiso: Boolean(gen.visor_sat_permiso),
        laboral_permiso: Boolean(gen.laboral_permiso),
        entitlements: gen.entitlements || defaultEntitlements(),
        skip_registry: !genSaveRegistry,
        registry_payment_status: genRegistryPay,
        registry_notes: genRegistryNotes,
        buyer_portal_username: pu || undefined,
        buyer_portal_password: pp.length >= 6 ? pp : undefined,
      };
      const ct0 = String(gen.contract_equipment_total ?? "").trim();
      const cs0 = String(gen.contract_equipment_slot ?? "").trim();
      if (ct0) body.contract_equipment_total = parseInt(ct0, 10);
      if (cs0) body.contract_equipment_slot = parseInt(cs0, 10);
      const { data } = await api.post("/system/license/generate", body);
      if (!data?.token) {
        toast.error("El servidor no devolvió token de licencia");
        return;
      }
      setGenToken(data.token);
      setLastEmitPortal({
        username: pu,
        password: pp.length >= 6 ? pp : "",
      });
      toast.success("Licencia emitida. Cópiala y envíala al cliente.");
      const rid = data.registry_id;
      const solFile = genEmitSolicitudPdfRef.current?.files?.[0];
      if (rid && solFile && genSaveRegistry) {
        setBusyGenSolicitudAttach(true);
        try {
          const ufd = new FormData();
          ufd.append("file", solFile);
          await api.put(`/distributor/license-registry/${rid}/solicitud-pdf`, ufd);
          toast.success("Solicitud de aceptación del cliente guardada en Licencias emitidas (reemplaza el PDF generado).");
        } catch (e3) {
          toast.error(
            (await detailFromAxiosError(e3)) ||
              formatApiError(e3.response?.data?.detail) ||
              "No se pudo adjuntar la solicitud de aceptación a la cartera",
          );
        } finally {
          setBusyGenSolicitudAttach(false);
          if (genEmitSolicitudPdfRef.current) genEmitSolicitudPdfRef.current.value = "";
        }
      }
      if (genAutoActivacionPdf && data.token) {
        try {
          const pdfBody = { license_token: data.token };
          if (pu) pdfBody.portal_username = pu;
          if (pp.length >= 6) pdfBody.portal_password = pp;
          const { data: blob } = await api.post(
            "/system/license/activacion-pdf",
            pdfBody,
            { responseType: "blob" },
          );
          await saveActivacionPdfBlob(blob, "PDF de activación descargado para el cliente");
        } catch (e2) {
          toast.error((await detailFromAxiosError(e2)) || "No se pudo descargar el PDF de activación");
        }
      }
      setGenPortalUsername("");
      setGenPortalPassword("");
      setGen((g) => {
        const ct = parseInt(String(g.contract_equipment_total ?? "").trim(), 10);
        const cs = parseInt(String(g.contract_equipment_slot ?? "").trim(), 10);
        const bump =
          Number.isFinite(ct) && Number.isFinite(cs) && cs < ct ? String(cs + 1) : g.contract_equipment_slot;
        return {
          ...g,
          installation_id: "",
          contract_equipment_slot: bump,
        };
      });
    } catch (e) {
      toast.error(
        (await detailFromAxiosError(e)) ||
          formatApiError(e.response?.data?.detail) ||
          "Error al generar la licencia",
      );
    } finally {
      setBusyGenerate(false);
    }
  };

  const lic = info?.license;
  const licPayload = lic?.payload || {};
  const statusColor = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-300",
    expiring_soon: "bg-amber-100 text-amber-800 border-amber-300",
    expired: "bg-red-100 text-red-800 border-red-300",
    missing: "bg-slate-100 text-slate-700 border-slate-300",
    invalid: "bg-red-100 text-red-800 border-red-300",
    invalid_hardware: "bg-purple-100 text-purple-800 border-purple-300",
  }[lic?.status] || "bg-slate-100";
  
  const statusLabel = {
    active: "Activa", expiring_soon: "Por expirar", expired: "Expirada",
    missing: "No instalada", invalid: "Inválida", invalid_hardware: "ID de Instalación no coincide"
  }[lic?.status] || lic?.status;

  const isBuyerCommercialFlow =
    !emisorView
    && canShowCommercialLicenseRequestUi(user, {
      workspaceMode: mode,
      canSwitch,
      buyerPreviewUx: buyerPreviewActive,
    });
  const hideBuyerTechnicalUi = resolveHideBuyerTechnicalUi({
    user,
    workspaceMode: mode,
    canSwitch,
    buyerPreviewUx: buyerPreviewActive,
    blockBuyerCommercialUi,
    emisorView,
    canGenerateLicenses: Boolean(info?.can_generate_licenses),
  });
  const hasWebLicenseActive =
    Boolean(lic?.present && ["active", "expiring_soon"].includes(String(lic?.status || "")))
    || hasActiveCommercialLicense(info);
  const showTechnicalRenewalPanel =
    !emisorView
    && !hideBuyerTechnicalUi
    && hasWebLicenseActive
    && Boolean(info)
    && (isOwner || canInstallLicense);

  if (loading) return <div className="text-center py-8 text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  if (loadError && !info) {
    return (
      <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-6 text-sm text-amber-950 space-y-3 max-w-lg">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">No se pudo cargar esta sección</p>
            <p className="text-xs mt-2 text-amber-900/90 whitespace-pre-wrap">{loadError}</p>
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" className="border-amber-400" onClick={() => load()}>
          Reintentar
        </Button>
      </div>
    );
  }

  const sub = info?.subscription;
  const trialEndsLabel = sub?.trial_ends_at ? String(sub.trial_ends_at).slice(0, 10) : "";

  const profileForSolic = info?.profile || {};
  const titularSolic =
    (profileForSolic.razon_social || "").trim() ||
    (licPayload.licensee_name || "").trim() ||
    (profileForSolic.nombre_comercial || "").trim() ||
    (profileForSolic.responsable || "").trim() ||
    "—";
  const rfcSolic =
    (profileForSolic.rfc || "").trim() ||
    (licPayload.licensee_rfc || "").trim() ||
    "—";
  const instSolic = (info?.installation_id || "").trim() || "—";

  const copySolicitudText = () => {
    const msg =
      `Solicitud de licencia — Visor Fiscal\n` +
      `ID instalación: ${instSolic}\n` +
      `RFC: ${rfcSolic}\n` +
      `Titular / razón social: ${titularSolic}\n\n` +
      `Solicito la clave de activación para esta instalación. ` +
      `(La vigencia y el plan los define el distribuidor al emitir la licencia.)`;
    navigator.clipboard.writeText(msg);
    toast.success("Texto copiado para enviar a tu distribuidor");
  };

  const downloadSolicitudPdf = async () => {
    setBusySolicPdf(true);
    try {
      const { data } = await api.get("/system/license/solicitud-pdf", { responseType: "blob" });
      const safeId = String(info?.installation_id || "visor").replace(/[^a-zA-Z0-9_-]/g, "-");
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `solicitud-licencia-${safeId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error((await detailFromAxiosError(e)) || formatApiError(e.response?.data?.detail) || "Error al generar PDF");
    } finally {
      setBusySolicPdf(false);
    }
  };

  const downloadActivacionPdf = async () => {
    const t = (genToken || "").trim();
    if (!t) return;
    setBusyActivacionPdf(true);
    try {
      const puEff = (genPortalUsername || lastEmitPortal.username || "").trim().toLowerCase();
      const ppEff = (genPortalPassword || lastEmitPortal.password || "").trim();
      const pdfBody = { license_token: t };
      if (puEff) pdfBody.portal_username = puEff;
      if (ppEff.length >= 6) pdfBody.portal_password = ppEff;
      const { data } = await api.post("/system/license/activacion-pdf", pdfBody, { responseType: "blob" });
      await saveActivacionPdfBlob(data, "PDF con clave descargado");
    } catch (e) {
      toast.error((await detailFromAxiosError(e)) || formatApiError(e.response?.data?.detail) || "Error al generar PDF");
    } finally {
      setBusyActivacionPdf(false);
    }
  };

  const onImportSolicitudPdf = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Selecciona un archivo PDF");
      return;
    }
    setBusyImportPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const url =
        (mode === "distribuidor" || isPlatformDesigner) && user?.can_generate_licenses
          ? "/distributor/import-solicitud-pdf"
          : "/system/license/import-solicitud-pdf";
      const { data } = await api.post(url, fd);
      setGen((prev) => {
        const entFromPdf =
          data.entitlements && typeof data.entitlements === "object"
            ? { ...defaultEntitlements(), ...data.entitlements }
            : null;
        const ent = entFromPdf || prev.entitlements || defaultEntitlements();
        return {
          ...prev,
          installation_id: data.installation_id || "",
          licensee_rfc: data.rfc || "",
          licensee_name: data.titular || "",
          licensee_email: data.licensee_email || prev.licensee_email,
          valid_from:
            (data.valid_from && String(data.valid_from).slice(0, 10)) || prev.valid_from,
          valid_to: (data.valid_to && String(data.valid_to).slice(0, 10)) || prev.valid_to,
          plan: (data.plan && String(data.plan)) || prev.plan,
          entitlements: ent,
          visor_sat_permiso: data.visor_sat_permiso ?? Boolean(ent.visor),
          laboral_permiso: data.laboral_permiso ?? Boolean(ent.laboral),
        };
      });
      const parts = ["Formulario listo para emitir"];
      if (data.buyer) {
        parts.push(
          data.buyer_created ? "Comprador nuevo en catálogo" : "Catálogo de compradores actualizado",
        );
      }
      toast.success(parts.join(" · "));
    } catch (err) {
      toast.error(
        (await detailFromAxiosError(err)) || formatApiError(err.response?.data?.detail) || "No se pudo leer el PDF",
      );
    } finally {
      setBusyImportPdf(false);
    }
  };

  const canEmitLicenses =
    isOwner && info?.can_generate_licenses && (mode === "distribuidor" || isPlatformDesigner);

  const suppressDesignerLicenseCtas = shouldSuppressLicenseCtasForPlatformDesigner(user, {
    buyerPreviewUx: buyerPreviewActive,
  });
  const showLicenseActivationPanelVisible = Boolean(
    info
    && !suppressDesignerLicenseCtas
    && showLicenseActivationPanel({ user, systemInfo: info, workspaceMode: mode, canSwitch }),
  );
  const effectiveBuyerPanelHint = Boolean(
    showBuyerPanelHint
    || (canInstallLicense && user?.is_admin_owner && showLicenseActivationPanelVisible),
  );

  return (
    <div
      className="space-y-5 max-w-5xl"
      data-testid={
        emisorView
          ? "config-system-tab-emisor"
          : hideBuyerTechnicalUi
            ? "config-system-tab-buyer-clean"
            : "config-system-tab-full"
      }
    >
      {!emisorView && !hideBuyerTechnicalUi && sub?.ok === false && sub?.reason && (
        <div className="rounded-sm border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-semibold">Acceso al sistema limitado</p>
          <p className="text-xs mt-1 text-amber-900">
            {sub.reason === "trial_expired"
              ? `La prueba gratuita (${info?.trial_days ?? 3} días) terminó. Pega la licencia que te envió el distribuidor abajo.`
              : sub.reason === "invalid_hardware"
                ? "La licencia guardada no corresponde a este equipo."
                : sub.reason === "expired"
                  ? "La licencia está vencida."
                  : "Instala una licencia válida para recuperar el uso completo."}
            {trialEndsLabel && sub.reason === "trial_expired" && (
              <> Fin de prueba: {trialEndsLabel}.</>
            )}
          </p>
        </div>
      )}
      {!emisorView && !hideBuyerTechnicalUi && sub?.ok && sub?.mode === "trial" && trialEndsLabel && (
        <div className="rounded-sm border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs text-blue-900">
          <span className="font-semibold">Periodo de prueba activo</span>
          {" — "}hasta el {trialEndsLabel} ({info?.trial_days ?? 3} días desde la primera ejecución). Después necesitarás una licencia.
        </div>
      )}
      {canEmitLicenses && (
        <div className="rounded-sm border border-violet-300 bg-violet-50/95 px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-violet-950 flex items-center gap-2">
                <UserPlus className="w-4 h-4 shrink-0" />
                Compradores del software (catálogo)
              </p>
              <p className="text-[11px] text-violet-900/90 mt-1 leading-snug">
                Alta manual de quien compra el sistema: datos comerciales, cobro opcional y usuario del portal del comprador.
                También puede crear o actualizar registros al <strong>importar el PDF de solicitud</strong> más abajo.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button asChild size="sm" className="h-9 bg-violet-700 hover:bg-violet-800 text-white rounded-sm">
                <Link to="/catalogo-compradores?alta=1">
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                  Dar de alta comprador
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="h-9 rounded-sm border-violet-300 bg-white">
                <Link to="/catalogo-compradores">Ver catálogo completo</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
      {!emisorView ? (
      <>
      {hideBuyerTechnicalUi ? (
        <BuyerMiNegocioView
          info={info}
          prof={prof}
          setProf={setProf}
          isOwner={isOwner}
          saveProfile={saveProfile}
          busyProf={busyProf}
          secRows={secRows}
          setSecRows={setSecRows}
          onClearSecurityRecovery={clearSecurityRecovery}
          user={user}
          lic={lic}
          licPayload={licPayload}
          statusLabel={statusLabel}
          statusColor={statusColor}
          moduleCatalog={moduleCatalog}
          onLicenseInstalled={({ info: newInfo, postInstallNotice }) => {
            setInfo(newInfo);
            if (postInstallNotice) setLicenseInstallNoticeOpen(true);
          }}
          canShowCommercial={canShowCommercialLicenseRequestUi(user, {
            workspaceMode: mode,
            canSwitch,
            buyerPreviewUx: buyerPreviewActive,
          })}
          suppressDesignerLicenseCtas={suppressDesignerLicenseCtas}
        />
      ) : (
      <>
      {/* Datos titular */}
      <div id="config-buyer-profile" className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 text-sm">
          <User className="w-4 h-4" /> {hideBuyerTechnicalUi ? "Datos del comprador" : "Datos del titular"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-5">
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-[#0F2D5C]">Nombre comercial (branding)</Label>
              <Input value={prof.nombre_comercial} onChange={(e)=>setProf({...prof, nombre_comercial: e.target.value})} disabled={!isOwner} placeholder="Ej. Mi Despacho Contable" className="mt-1" />
            </div>
            
            <div className="pt-2 border-t border-slate-50">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">Datos Fiscales y Facturación</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Razón social</Label>
                  <Input value={prof.razon_social} onChange={(e)=>setProf({...prof, razon_social: e.target.value})} disabled={!isOwner} className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">RFC</Label>
                    <Input value={prof.rfc} onChange={(e)=>setProf({...prof, rfc: e.target.value.toUpperCase()})} disabled={!isOwner} className="mt-1 font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs">Código Postal</Label>
                    <Input value={prof.codigo_postal} onChange={(e)=>setProf({...prof, codigo_postal: e.target.value})} disabled={!isOwner} maxLength={5} className="mt-1 font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Correo de facturación</Label>
                  <Input value={prof.correo_facturacion} onChange={(e)=>setProf({...prof, correo_facturacion: e.target.value})} disabled={!isOwner} className="mt-1 font-mono" placeholder="correo@ejemplo.com" />
                </div>
                <div>
                  <Label className="text-xs">Régimen fiscal (SAT)</Label>
                  <Select value={prof.regimen_fiscal} onValueChange={(v)=>setProf({...prof, regimen_fiscal: v})} disabled={!isOwner}>
                    <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue placeholder="Selecciona régimen" /></SelectTrigger>
                    <SelectContent>
                      {REGIMENES_SAT.map(r => (
                        <SelectItem key={r.c} value={r.c}>{formatRegimenSatSelectLabel(r)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Domicilio fiscal</Label>
                  <Input value={prof.domicilio} onChange={(e)=>setProf({...prof, domicilio: e.target.value})} disabled={!isOwner} placeholder="Dirección completa" className="mt-1" />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-50">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">Contacto y Responsable</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Responsable</Label>
                  <Input value={prof.responsable} onChange={(e)=>setProf({...prof, responsable: e.target.value})} disabled={!isOwner} placeholder="Nombre del contador responsable" className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Correo de contacto</Label>
                    <Input value={prof.email} onChange={(e)=>setProf({...prof, email: e.target.value})} disabled={!isOwner} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Teléfono de contacto</Label>
                    <Input value={prof.telefono} onChange={(e)=>setProf({...prof, telefono: e.target.value})} disabled={!isOwner} placeholder="81 1234 5678" className="mt-1" />
                  </div>
                </div>
              </div>
            </div>

            {isOwner && mode === "despacho" ? (
              <div className="pt-2 border-t border-slate-50">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5" /> Recuperación de contraseña
                </p>
                <p className="text-[10px] text-slate-600 mb-3 leading-snug">
                  Seleccione <strong>3 preguntas</strong> del catálogo y una respuesta por cada una.
                  En <strong>Olvidé mi contraseña</strong> el usuario elige una pregunta y escribe la respuesta correcta.
                  {info?.security_recovery?.configured ? (
                    <span className="text-emerald-800 font-semibold"> — Personalizado activo.</span>
                  ) : (
                    <span className="text-amber-800 font-semibold"> — Aún no configurado (preguntas de demostración).</span>
                  )}
                </p>
                <div className="space-y-4">
                  <SecurityRecoveryQuestionFields
                    rows={secRows}
                    onChange={setSecRows}
                    disabled={busyProf}
                    testIdPrefix="config-security"
                  />
                </div>
                {info?.security_recovery?.configured ? (
                  <div className="mt-3 flex justify-end">
                    <Button type="button" variant="outline" size="sm" className="h-8 text-[11px]" onClick={clearSecurityRecovery} disabled={busyProf}>
                      Quitar personalización (volver a demostración)
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {(isOwner || user?.role === "buyer_admin") && mode === "despacho" && !hideBuyerTechnicalUi ? (
              <div className="pt-2 border-t border-slate-50">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5" /> Gestión de llaves (servidor)
                </p>
                {securityKeysError ? (
                  <p className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-2 mb-2">{securityKeysError}</p>
                ) : null}
                {securityKeys ? (
                  <div className="space-y-2 text-[10px]">
                    <div
                      className={`rounded-sm border p-3 ${
                        securityKeys.fiel_encryption_key?.valid
                          ? "border-emerald-200 bg-emerald-50/80 text-emerald-950"
                          : "border-rose-200 bg-rose-50/80 text-rose-950"
                      }`}
                    >
                      <div className="font-bold flex items-center gap-1.5">
                        {securityKeys.fiel_encryption_key?.valid ? (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5" />
                        )}
                        FIEL_ENCRYPTION_KEY
                      </div>
                      <p className="mt-1 leading-snug">{securityKeys.fiel_encryption_key?.message}</p>
                      {securityKeys.fiel_encryption_key?.fingerprint ? (
                        <p className="mt-1 font-mono text-[9px] opacity-80">
                          Huella: {securityKeys.fiel_encryption_key.fingerprint}
                        </p>
                      ) : null}
                      <p className="mt-1 text-slate-600">
                        Longitud esperada: {securityKeys.fiel_encryption_key?.expected_length ?? 44} caracteres Fernet.
                      </p>
                    </div>
                    <div
                      className={`rounded-sm border p-3 ${
                        securityKeys.storage?.ok
                          ? "border-slate-200 bg-slate-50/80"
                          : "border-amber-200 bg-amber-50/80"
                      }`}
                    >
                      <div className="font-bold">Almacén bnt_storage</div>
                      <p className="mt-1">{securityKeys.storage?.message}</p>
                      {securityKeys.storage?.root ? (
                        <p className="mt-1 font-mono text-[9px] break-all">{securityKeys.storage.root}</p>
                      ) : null}
                    </div>
                    <div className="rounded-sm border border-blue-200 bg-blue-50/80 p-3 text-blue-950">
                      <div className="font-bold">Política CIEC</div>
                      <p className="mt-1 leading-snug">{securityKeys.ciec_policy?.message}</p>
                    </div>
                    <div
                      className={`rounded-sm border p-2 font-semibold text-center ${
                        securityKeys.production_ready
                          ? "border-emerald-300 bg-emerald-100 text-emerald-900"
                          : "border-amber-300 bg-amber-100 text-amber-900"
                      }`}
                    >
                      {securityKeys.production_ready
                        ? "Listo para descargas SAT seguras"
                        : "Revise llave de cifrado y almacén antes de producción"}
                    </div>
                    {securityKeys.sat_downloads_active?.active ? (
                      <p className="text-amber-800 text-[10px]">
                        Hay descarga SAT activa — el mantenimiento automático permanece bloqueado hasta que termine.
                      </p>
                    ) : null}
                  </div>
                ) : !securityKeysError ? (
                  <p className="text-[10px] text-slate-500">Cargando estado de llaves…</p>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <Label className="text-xs font-semibold text-[#0F2D5C]">Logo del sistema</Label>
            <div className="flex flex-col items-center gap-4 p-6 border border-dashed border-slate-200 rounded-sm bg-slate-50/50">
              {prof.logo_base64 ? (
                <div className="relative group">
                  <img src={prof.logo_base64} alt="Logo" className="max-h-32 max-w-full object-contain rounded-sm shadow-sm" />
                  {isOwner && (
                    <button 
                      onClick={() => setProf({...prof, logo_base64: ""})}
                      className="absolute -top-2 -right-2 bg-rose-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-slate-300 flex flex-col items-center gap-2 py-4">
                  <Building2 className="w-16 h-16" />
                  <span className="text-[10px] uppercase tracking-widest font-bold">Sin logo personalizado</span>
                </div>
              )}
              {isOwner && (
                <div className="w-full">
                  <input 
                    type="file" 
                    id="logo-upload" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onloadend = () => setProf({...prof, logo_base64: reader.result});
                      reader.readAsDataURL(file);
                    }}
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full h-9 text-[11px] font-semibold" 
                    onClick={() => document.getElementById('logo-upload').click()}
                  >
                    {prof.logo_base64 ? "Cambiar logo" : "Subir logo"}
                  </Button>
                </div>
              )}
            </div>
            <p className="text-[10px] text-slate-400 text-center italic">Se recomienda una imagen PNG o SVG con fondo transparente.</p>
            
            <div className="pt-6 border-t border-slate-100 mt-4">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-center">
                <p className="text-[11px] font-bold text-[#0F2D5C] uppercase tracking-widest mb-2">ID de Instalación Único</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="text-sm font-mono font-bold text-[#3DA873] bg-white px-3 py-1.5 border border-slate-200 rounded-sm">
                    {info?.installation_id || "Generando..."}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    onClick={() => {
                      navigator.clipboard.writeText(info?.installation_id);
                      toast.success("ID copiado al portapapeles");
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-[9px] text-slate-500 mt-2 italic">
                  {hideBuyerTechnicalUi
                    ? "Identificador de tu instalación web BNT."
                    : "Envía este código al diseñador para solicitar tu licencia de activación."}
                </p>
                {showLicenseSigningHints && info?.license_signing_fingerprint && !hideBuyerTechnicalUi ? (
                  <div className="mt-3 rounded-sm border border-amber-200 bg-amber-50/80 px-2 py-2 text-[9px] text-amber-950 text-left">
                    <span className="font-semibold">Huella de firma de licencias</span>
                    <code className="block font-mono mt-0.5 break-all select-all">{info.license_signing_fingerprint}</code>
                    <p className="mt-1 text-amber-900/90 leading-snug">
                      Idealmente la misma <code className="font-mono">LICENSE_SIGNING_KEY</code> que en el equipo emisor. Si no
                      puedes cambiarla, en el <code className="font-mono">.env</code> del backend puedes añadir{" "}
                      <code className="font-mono">LICENSE_SIGNING_KEYS</code>
                      <span className="font-normal">
                        {" "}
                        con el <strong>mismo texto</strong> que el <code className="font-mono">LICENSE_SIGNING_KEY</code> del
                        distribuidor (coma si son varios), <strong>sin comillas</strong>, guardar, <strong>cerrar y volver a abrir</strong>{" "}
                        el programa y probar de nuevo.
                      </span>
                    </p>
                    {Array.isArray(info?.license_signing_verify_fingerprints) &&
                    info.license_signing_verify_fingerprints.length > 0 ? (
                      <div className="mt-2 pt-2 border-t border-amber-200/80">
                        <span className="font-semibold text-amber-950">Huellas que acepta esta instalación al activar:</span>
                        <div className="font-mono text-[8px] mt-0.5 break-all text-amber-900 select-all">
                          {info.license_signing_verify_fingerprints.join(" · ")}
                        </div>
                        {info.license_signing_verify_fingerprints.length === 1 ? (
                          <p className="mt-1 text-amber-900/85 leading-snug">
                            Solo aparece una: el PDF del distribuidor debe mostrar <strong>exactamente</strong> esta huella, o bien
                            configura <code className="font-mono">LICENSE_SIGNING_KEYS</code>. En el .exe de Windows el archivo suele
                            ser el <code className="font-mono">.env</code> junto a <code className="font-mono">VisorFiscal.exe</code>.
                          </p>
                        ) : (
                          <p className="mt-1 text-amber-900/85 leading-snug">
                            La fila «Huella firma (emisor)» del PDF debe coincidir con alguna de las de arriba. Si no aparece, la clave
                            añadida no es correcta o el servidor no recargó el .env.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        {isOwner && (
          <div className="pt-4 border-t border-slate-100 flex justify-end">
            <Button onClick={saveProfile} disabled={busyProf} className="h-10 px-8 bg-[#0F2D5C] hover:bg-[#0a1f42] font-bold shadow-md">
              {busyProf && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar todos los cambios
            </Button>
          </div>
        )}
      </div>

      {/* Licencia */}
      <div ref={solicitudLicRef} className="bg-white border border-slate-200 rounded-sm p-5">
        {showSolicitudLicenciaBanner && canInstallLicense && !isBuyerCommercialFlow ? (
          <div className="mb-4 rounded-sm border border-sky-200 bg-sky-50/90 px-3 py-3 text-xs text-sky-950 space-y-2">
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-sky-700 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-sky-950">Datos para solicitar tu licencia</p>
                <p className="text-[11px] text-sky-900/90 mt-1">
                  Puedes <strong>copiar el texto</strong> o <strong>descargar un PDF</strong> para enviarlo por correo o WhatsApp.
                  Cuando recibas la clave, pégala abajo en <strong>Pegar clave de licencia</strong> (si el distribuidor te envía un PDF, abre el archivo y copia la clave completa).
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] font-mono bg-white/80 rounded-sm border border-sky-100 px-2 py-2">
              <div>
                <span className="text-sky-700 font-sans font-semibold">ID instalación</span>
                <div className="break-all">{instSolic}</div>
              </div>
              <div>
                <span className="text-sky-700 font-sans font-semibold">RFC</span>
                <div>{rfcSolic}</div>
              </div>
              <div className="sm:col-span-1">
                <span className="text-sky-700 font-sans font-semibold">Titular</span>
                <div className="font-sans normal-case">{titularSolic}</div>
              </div>
            </div>
            <p className="text-[10px] text-sky-900/80">
              La <strong>vigencia</strong> y el <strong>plan</strong> los indica y registra el distribuidor al generar tu licencia; este PDF solo lleva los datos de tu instalación y perfil.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-[11px] border-sky-300"
                onClick={copySolicitudText}
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar texto
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-[11px] border-sky-300 bg-white"
                onClick={downloadSolicitudPdf}
                disabled={busySolicPdf}
              >
                {busySolicPdf ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <FileDown className="w-3.5 h-3.5 mr-1.5" />
                )}
                Descargar PDF para el distribuidor
              </Button>
            </div>
          </div>
        ) : null}
        <div className="flex items-start justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
            <ShieldCheck className="w-4 h-4" /> Licencia actual
          </h3>
          <span className={`text-xs px-2.5 py-1 rounded-sm border font-semibold ${statusColor}`}>{statusLabel}</span>
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <InfoCell label="Titular licencia" value={licPayload.licensee_name || "—"} />
          <InfoCell label="RFC" value={licPayload.licensee_rfc || "—"} mono />
          <InfoCell label="Plan" value={licPayload.plan || "—"} />
          <InfoCell label="Versión app" value={info?.version || "1.0"} />
          <InfoCell
            label="Vigencia desde"
            value={
              licPayload.valid_from ? formatLicenseDateForDisplay(licPayload.valid_from) : "—"
            }
            subvalue={licPayload.valid_from ? String(licPayload.valid_from).slice(0, 10) : undefined}
          />
          <InfoCell
            label="Vigencia hasta"
            value={licPayload.valid_to ? formatLicenseDateForDisplay(licPayload.valid_to) : "—"}
            subvalue={licPayload.valid_to ? String(licPayload.valid_to).slice(0, 10) : undefined}
          />
          <InfoCell label="Instalada" value={(lic?.installed_at || "").slice(0, 10) || "—"} />
          <InfoCell label="ID licencia" value={(licPayload.license_id || "").slice(0, 8) || "—"} mono />
        </div>
        <ActiveLicenseModulesList systemInfo={info} moduleCatalog={moduleCatalog} />
        {showTechnicalRenewalPanel ? (
          <div id="buyer-license-renewal" className="mt-4 pt-4 border-t border-slate-200" data-testid="config-license-renewal-panel">
            <LicenseRenewalExpansionPanel
              systemInfo={info}
              profileDefaults={prof}
              forceVisible
              modalFlow
              showHistory={Boolean(renewalDeepLink.openHistory)}
              prefill={renewalDeepLink.prefill}
              onFormClose={clearRenewalDeepLink}
              onPrefillConsumed={clearRenewalDeepLink}
            />
          </div>
        ) : null}
        {(isOwner || canInstallLicense) && effectiveBuyerPanelHint && !hideBuyerTechnicalUi ? (
          <div className="mt-3 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] text-slate-700">
            <span className="font-semibold text-slate-800">Panel y aquí</span>
            {" — "}También puedes <strong>solicitar licencia</strong>, <strong>importar el PDF de activación</strong> o{" "}
            <strong>pegar la clave</strong> desde el{" "}
            <Link to="/" className="text-[#0F2D5C] font-semibold underline">
              Panel general
            </Link>
            . En esta pantalla tienes el PDF y el pegado manual sin salir de Configuración.
          </div>
        ) : null}
        {(() => {
          const showActivationPanel =
            showLicenseActivationPanelVisible && !isBuyerCommercialFlow;
          const showStandaloneCommercialRequest =
            info
            && !suppressDesignerLicenseCtas
            && canShowCommercialLicenseRequestUi(user, { workspaceMode: mode, canSwitch })
            && !showActivationPanel;
          return (
            <>
              {showStandaloneCommercialRequest ? (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <LicenseCommercialSection
                    systemInfo={info}
                    profileDefaults={prof}
                    onLicenseInstalled={({ info: newInfo, postInstallNotice }) => {
                      setInfo(newInfo);
                      if (postInstallNotice) setLicenseInstallNoticeOpen(true);
                    }}
                  />
                </div>
              ) : null}
              {showActivationPanel ? (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <LicenseActivationPanel
                    systemInfo={info}
                    profileDefaults={prof}
                    showAlerts={false}
                    onLicenseInstalled={({ info: newInfo, postInstallNotice }) => {
                      setInfo(newInfo);
                      if (postInstallNotice) setLicenseInstallNoticeOpen(true);
                    }}
                  />
                </div>
              ) : null}
            </>
          );
        })()}
        {(isOwner || canInstallLicense) && !hideBuyerTechnicalUi && (
          <div ref={activarLicRef} className="mt-4 pt-4 border-t border-slate-200 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-xs">Pegar clave de licencia</Label>
              <div className="flex items-center gap-2">
                <input
                  ref={activacionLicenciaPdfInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={onImportActivacionLicenciaPdf}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-[11px] border-emerald-600 text-emerald-800 hover:bg-emerald-50"
                  disabled={busyImportActivacionLicenciaPdf || busyLic}
                  onClick={() => activacionLicenciaPdfInputRef.current?.click()}
                >
                  {busyImportActivacionLicenciaPdf ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Importar PDF de activación
                </Button>
              </div>
            </div>
            <SpellcheckTextarea
              value={licToken}
              onChange={(e) => setLicToken(e.target.value)}
              rows={3}
              placeholder="base64.firma — pega aquí el token que te enviamos"
              className="w-full rounded-sm border border-slate-300 px-3 py-2 text-xs font-mono resize-none focus:border-[#0F2D5C] focus:ring-1 focus:ring-[#0F2D5C] outline-none"
            />
            <Button onClick={installLicense} disabled={busyLic} className="h-9 bg-[#3DA873] hover:bg-[#3DA873]/90">
              {busyLic && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {lic?.present ? "Reemplazar licencia" : "Instalar licencia"}
            </Button>
          </div>
        )}
      </div>

      {isPlatformDesigner ? (
        <div
          className="rounded-sm border border-sky-200 bg-sky-50 px-4 py-3 text-xs text-sky-950"
          data-testid="config-platform-designer-banner"
        >
          <p className="font-semibold">Modo Plataforma BNT</p>
          <p className="mt-1 text-sky-900">
            Superusuario — sin selector Despacho/Distribuidor ni revisión de compradores.
          </p>
        </div>
      ) : null}
      {isOwner && info?.can_generate_licenses && canSwitch && mode === "despacho" && !isPlatformDesigner ? (
        <div className="rounded-sm border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-700">
          <p className="font-semibold text-slate-800">Modo Despacho activo</p>
          <p className="mt-1 text-slate-600">
            Para <strong>emitir licencias</strong> a quienes compran el sistema, cambia a{" "}
            <strong>Distribuidor (vender sistema)</strong>:
          </p>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="mt-2 h-9 max-w-sm bg-white text-xs">
              <SelectValue placeholder="Modo de trabajo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="despacho">Despacho (mi cartera)</SelectItem>
              <SelectItem value="distribuidor">Distribuidor (vender sistema)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}
      {isOwner && info?.can_generate_licenses && canSwitch && mode === "distribuidor" && !isPlatformDesigner ? (
        <div className="rounded-sm border border-violet-200 bg-violet-50 px-4 py-3 text-xs text-violet-950">
          <p className="font-semibold">Modo Distribuidor activo</p>
          <p className="mt-1 text-violet-900">
            Para volver a tu cartera contable, elige <strong>Despacho (mi cartera)</strong>:
          </p>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="mt-2 h-9 max-w-sm bg-white text-xs">
              <SelectValue placeholder="Modo de trabajo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="despacho">Despacho (mi cartera)</SelectItem>
              <SelectItem value="distribuidor">Distribuidor (vender sistema)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}
      </>
      )}
      </>
      ) : null}
      {emisorView && showLicenseSigningHints && info?.license_signing_fingerprint ? (
        <div className="rounded-sm border border-amber-200 bg-amber-50/80 px-4 py-3 text-xs text-amber-950">
          <span className="font-semibold">Huella de firma de licencias (emisor)</span>
          <code className="block font-mono mt-1 break-all select-all">{info.license_signing_fingerprint}</code>
          {Array.isArray(info?.license_signing_verify_fingerprints) &&
          info.license_signing_verify_fingerprints.length > 0 ? (
            <div className="mt-2 pt-2 border-t border-amber-200/80">
              <span className="font-semibold">Huellas que acepta esta instalación al activar:</span>
              <div className="font-mono text-[10px] mt-0.5 break-all">
                {info.license_signing_verify_fingerprints.join(" · ")}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {emisorView && !loading && info && !canEmitLicenses ? (
        <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-4 text-sm text-amber-950">
          <p className="font-semibold m-0">Emisión no disponible en esta sesión</p>
          <p className="text-xs mt-2 m-0 leading-relaxed">
            Solo el titular con permiso de generación de licencias puede emitir tokens. Si necesita configurar usuarios o
            datos del negocio, use los accesos del aviso superior.
          </p>
        </div>
      ) : null}
      {/* Emitir licencia (instalaciones con ALLOW_LICENSE_GENERATION; típicamente el distribuidor) */}
      {canEmitLicenses && (
        <div className="bg-white border border-violet-200 rounded-sm p-5" data-testid="config-emisor-emit-panel">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm mb-1">
            <ServerCog className="w-4 h-4 text-violet-700" /> Emitir licencias para clientes
          </h3>
          <p className="text-[11px] text-slate-500 mb-3">
            Genera un token firmado ligado al <strong>ID de instalación</strong> del cliente y al periodo de vigencia acordado.
            En builds para usuarios finales suele estar deshabilitado (<code className="font-mono">ALLOW_LICENSE_GENERATION=false</code>).
          </p>
          <div className="rounded-sm border border-sky-200 bg-sky-50/90 px-3 py-2.5 text-[10px] text-sky-950 leading-snug mb-3">
            <p className="font-semibold text-sky-950">Mismo comprador, varias computadoras</p>
            <p className="mt-1 text-sky-900/95">
              Un solo <strong>RFC / comprador</strong> en catálogo puede tener <strong>varias licencias</strong>: cada equipo tiene su propio{" "}
              <strong>ID de instalación</strong> (VF-…). Importa o pega el ID de <strong>cada PC</strong>, emite una clave por equipo y registra (opcional){""}
              <strong> cuántos equipos contrató</strong> y <strong>cuál equipo es este</strong> (1 de 3, 2 de 3, …) para seguimiento en cartera.
              Los datos fiscales de empresas cliente se gestionan <strong>por instalación</strong>; no se sincronizan solas entre equipos.
            </p>
          </div>
          <div className="space-y-4">
            <div className="rounded-sm border border-violet-200 bg-violet-50/60 p-3 space-y-2">
              <div className="text-[10px] font-semibold text-violet-900 uppercase tracking-wide">PDF de solicitud del cliente</div>
              <p className="text-[10px] text-slate-600 leading-snug">
                Si el cliente te envió el PDF generado desde <strong>Configuración</strong> en su equipo, impórtalo para rellenar{" "}
                <strong>ID de instalación</strong>, <strong>RFC</strong> y <strong>razón social</strong>, y{" "}
                <strong className="text-violet-900">dar de alta o actualizar al comprador en el catálogo</strong>. También puedes{" "}
                <Link to="/catalogo-compradores" className="text-violet-800 font-semibold underline">
                  agregar un comprador manualmente
                </Link>{" "}
                antes de emitir.
              </p>
              <input
                ref={solicitudPdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={onImportSolicitudPdf}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-[11px] border-violet-300 bg-white"
                onClick={() => solicitudPdfInputRef.current?.click()}
                disabled={busyImportPdf}
              >
                {busyImportPdf ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                Importar PDF de solicitud
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Correo del cliente (opcional)</Label>
                <Input value={gen.licensee_email} onChange={(e)=>setGen({...gen, licensee_email: e.target.value})} className="h-8 text-xs font-mono" placeholder="contacto@cliente.com" />
              </div>
              <div>
                <Label className="text-xs">Razón social del cliente</Label>
                <Input value={gen.licensee_name} onChange={(e)=>setGen({...gen, licensee_name: e.target.value})} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">RFC</Label>
                <Input value={gen.licensee_rfc} onChange={(e)=>setGen({...gen, licensee_rfc: e.target.value.toUpperCase()})} className="h-8 text-xs font-mono" />
              </div>
              <div>
                <Label className="text-xs">ID de Instalación (del equipo del cliente)</Label>
                <Input value={gen.installation_id} onChange={(e)=>setGen({...gen, installation_id: e.target.value})} className="h-8 text-xs font-mono" placeholder="VF-XXXX-XXXX" />
              </div>
              <div>
                <Label className="text-xs">Equipos del contrato (opcional)</Label>
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={gen.contract_equipment_total}
                  onChange={(e)=>setGen({...gen, contract_equipment_total: e.target.value})}
                  className="h-8 text-xs font-mono"
                  placeholder="Ej. 3 si vendió 3 licencias"
                />
                <p className="text-[9px] text-slate-500 mt-0.5">Se guarda en el comprador (máx. registrado).</p>
              </div>
              <div>
                <Label className="text-xs">Esta licencia es el equipo (opcional)</Label>
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={gen.contract_equipment_slot}
                  onChange={(e)=>setGen({...gen, contract_equipment_slot: e.target.value})}
                  className="h-8 text-xs font-mono"
                  placeholder="1, 2, 3…"
                />
                <p className="text-[9px] text-slate-500 mt-0.5">Debe ser ≤ equipos del contrato.</p>
              </div>
              <div>
                <Label className="text-xs">Válida desde</Label>
                <Input type="date" value={gen.valid_from} onChange={(e)=>setGen({...gen, valid_from: e.target.value})} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Válida hasta</Label>
                <Input type="date" value={gen.valid_to} onChange={(e)=>setGen({...gen, valid_to: e.target.value})} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Plan</Label>
                <Select value={gen.plan} onValueChange={(v)=>setGen({...gen, plan: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3 space-y-2 rounded-sm border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] font-semibold text-slate-700 uppercase tracking-wide">Módulos incluidos en la licencia (v2)</div>
                {moduleCatalog.map((mod) => (
                  <label key={mod.id} className="flex items-start gap-2 text-xs text-slate-700">
                    <Checkbox
                      className="mt-0.5"
                      checked={Boolean((gen.entitlements || {})[mod.id])}
                      onCheckedChange={(v) => setEntitlement(mod.id, !!v)}
                    />
                    <span>
                      <span className="font-medium">{mod.label}</span>
                      {mod.description ? (
                        <span className="block text-[10px] text-slate-500 leading-snug">{mod.description}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-4 space-y-2 rounded-sm border border-violet-100 bg-violet-50/40 p-3">
              <div className="text-[10px] font-semibold text-violet-900 uppercase tracking-wide">Acceso web del comprador (opcional)</div>
              <p className="text-[10px] text-slate-600 leading-snug">
                Usuario y contraseña para que el cliente entre a la misma pantalla de acceso y gestione su despacho.
                Si no las defines aquí, puedes hacerlo en <strong>Catálogo de compradores</strong>.
                La prueba del ejecutable sigue controlada por la <strong>licencia</strong>.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Usuario de acceso</Label>
                  <Input
                    value={genPortalUsername}
                    onChange={(e) => setGenPortalUsername(e.target.value.toLowerCase().replace(/\s+/g, ""))}
                    className="h-8 text-xs font-mono mt-1"
                    placeholder="ej. acmex_sa"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label className="text-xs">Contraseña (mín. 6, si hay usuario)</Label>
                  <Input
                    type="password"
                    value={genPortalPassword}
                    onChange={(e) => setGenPortalPassword(e.target.value)}
                    className="h-8 text-xs font-mono mt-1"
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-3 rounded-sm border border-slate-200 bg-slate-50/80 p-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="gen-save-registry"
                  checked={genSaveRegistry}
                  onCheckedChange={(v) => setGenSaveRegistry(!!v)}
                />
                <label htmlFor="gen-save-registry" className="text-xs leading-snug cursor-pointer">
                  <span className="font-semibold text-slate-800">Guardar en cartera de licencias</span>
                  <span className="block text-slate-600 mt-0.5">
                    Registra vigencia, titular e ID de instalación para seguimiento y recordatorios de vencimiento (pestaña Cartera de licencias).
                  </span>
                </label>
              </div>
              {genSaveRegistry && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                  <div>
                    <Label className="text-xs">Estado de pago (inicial)</Label>
                    <Select value={genRegistryPay} onValueChange={setGenRegistryPay}>
                      <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="pagado">Pagado</SelectItem>
                        <SelectItem value="parcial">Parcial</SelectItem>
                        <SelectItem value="cortesia">Cortesía</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Notas (opcional)</Label>
                    <SpellcheckTextarea
                      value={genRegistryNotes}
                      onChange={(e) => setGenRegistryNotes(e.target.value)}
                      rows={2}
                      placeholder="Referencia de factura, acuerdos, etc."
                      className="w-full mt-1 rounded-sm border border-slate-300 px-2 py-1.5 text-[11px] resize-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">PDF de solicitud firmado del cliente (opcional)</Label>
                    <input
                      ref={genEmitSolicitudPdfRef}
                      type="file"
                      accept="application/pdf,.pdf"
                      className="mt-1 block w-full text-[11px] text-slate-600 file:mr-2 file:rounded-sm file:border file:border-slate-200 file:bg-white file:px-2 file:py-1 file:text-[11px]"
                    />
                    <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                      Al emitir ya se guarda en <strong>Licencias emitidas</strong> un PDF de solicitud con declaración de aceptación.
                      Si el comprador le envió su PDF firmado desde su programa, adjúntelo aquí para <strong>reemplazar</strong> ese archivo
                      (máx. ~3 MB).
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-start gap-2 pt-1">
              <Checkbox id="gen-auto-pdf-cliente" checked={genAutoActivacionPdf} onCheckedChange={(v) => setGenAutoActivacionPdf(!!v)} />
              <label htmlFor="gen-auto-pdf-cliente" className="text-[11px] text-slate-600 cursor-pointer leading-snug">
                Al <strong>generar el token</strong>, descargar automáticamente el <strong>PDF con la clave</strong> para reenviarlo al cliente
                (puedes volver a descargarlo con el botón de abajo).
              </label>
            </div>
            <Button
              onClick={generateLicense}
              disabled={busyGenerate || busyGenSolicitudAttach}
              className="h-9 inline-flex items-center bg-violet-700 hover:bg-violet-800 text-xs font-bold disabled:opacity-60"
            >
              {busyGenerate || busyGenSolicitudAttach ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
              Generar token de activación
            </Button>
            {genToken && (
              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-700">Token de licencia (envíalo al cliente):</span>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { navigator.clipboard.writeText(genToken); toast.success("Copiada al portapapeles"); }}
                      className="h-7 text-[11px]"
                    >
                      <Copy className="w-3 h-3 mr-1" /> Copiar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={downloadActivacionPdf}
                      disabled={busyActivacionPdf}
                      className="h-7 text-[11px]"
                    >
                      {busyActivacionPdf ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileDown className="w-3 h-3 mr-1" />}
                      PDF para el cliente
                    </Button>
                  </div>
                </div>
                <textarea readOnly value={genToken} rows={4} className="w-full rounded-sm border border-slate-300 px-2 py-1.5 text-[10px] font-mono resize-none bg-white" />
                <p className="text-[10px] text-slate-500 mt-2 leading-snug">
                  El PDF incluye el <strong>plan</strong> y las fechas de <strong>vigencia</strong> según la clave. Para que el archivo lleve también el <strong>usuario y la contraseña</strong> del administrador comprador, descárgalo justo al emitir (casilla de arriba) o escribe de nuevo esos datos en «Acceso web del comprador» y pulsa «PDF para el cliente».
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={licenseInstallNoticeOpen} onOpenChange={setLicenseInstallNoticeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{LICENSE_INSTALL_NOTICE_TITLE}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-700">
                {LICENSE_INSTALL_NOTICE_PARAS.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="bg-[#0F2D5C]">Entendido</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InfoCell({ label, value, mono, subvalue }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 text-slate-900 ${mono ? "font-mono text-[11px]" : "text-xs"}`}>{value}</div>
      {subvalue ? (
        <div
          className="text-[10px] text-slate-500 font-mono mt-0.5 select-all"
          title="Referencia año-mes-día (ISO)"
        >
          {subvalue}
        </div>
      ) : null}
    </div>
  );
}

const EMPTY_RESELLER = {
  requires_invoice: false,
  datos_generales: "",
  factura_rfc: "",
  factura_razon_social: "",
  factura_email: "",
  factura_codigo_postal: "",
};

function normalizeReseller(r) {
  if (!r || typeof r !== "object") return { ...EMPTY_RESELLER };
  return {
    requires_invoice: !!r.requires_invoice,
    datos_generales: r.datos_generales || "",
    factura_rfc: r.factura_rfc || "",
    factura_razon_social: r.factura_razon_social || "",
    factura_email: r.factura_email || "",
    factura_codigo_postal: r.factura_codigo_postal || "",
  };
}

/** Bloque: factura sí/no, datos fiscales si aplica, datos generales (solo UI revendedor). */
function ResellerProfileFields({ value, onChange, idPrefix }) {
  return (
    <div className="border border-violet-100 bg-violet-50/40 rounded-sm p-3 space-y-3">
      <div className="text-[10px] font-semibold text-violet-900 uppercase tracking-wider">Perfil revendedor</div>
      <div>
        <Label className="text-xs">¿Requiere factura?</Label>
        <RadioGroup
          value={value.requires_invoice ? "si" : "no"}
          onValueChange={(v) => onChange({ ...value, requires_invoice: v === "si" })}
          className="flex gap-4 mt-2"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id={`${idPrefix}-fac-no`} />
            <Label htmlFor={`${idPrefix}-fac-no`} className="text-xs font-normal cursor-pointer">
              No
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="si" id={`${idPrefix}-fac-si`} />
            <Label htmlFor={`${idPrefix}-fac-si`} className="text-xs font-normal cursor-pointer">
              Sí
            </Label>
          </div>
        </RadioGroup>
      </div>
      {value.requires_invoice ? (
        <div className="grid grid-cols-1 gap-2">
          <div>
            <Label className="text-[10px]">RFC (facturación)</Label>
            <Input
              value={value.factura_rfc}
              onChange={(e) => onChange({ ...value, factura_rfc: e.target.value.toUpperCase() })}
              className="h-8 mt-1 text-xs font-mono"
            />
          </div>
          <div>
            <Label className="text-[10px]">Razón social</Label>
            <Input
              value={value.factura_razon_social}
              onChange={(e) => onChange({ ...value, factura_razon_social: e.target.value })}
              className="h-8 mt-1 text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Correo fiscal</Label>
              <Input
                type="email"
                value={value.factura_email}
                onChange={(e) => onChange({ ...value, factura_email: e.target.value })}
                className="h-8 mt-1 text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px]">C.P.</Label>
              <Input
                value={value.factura_codigo_postal}
                onChange={(e) => onChange({ ...value, factura_codigo_postal: e.target.value })}
                className="h-8 mt-1 text-xs"
              />
            </div>
          </div>
        </div>
      ) : null}
      <div>
        <Label className="text-[10px]">Datos generales (contacto, notas)</Label>
        <SpellcheckTextarea
          value={value.datos_generales}
          onChange={(e) => onChange({ ...value, datos_generales: e.target.value })}
          rows={3}
          className="w-full mt-1 rounded-sm border border-slate-300 px-2 py-1.5 text-[11px] resize-none"
          placeholder="Teléfono, domicilio, persona de contacto, acuerdos comerciales…"
        />
      </div>
    </div>
  );
}

function DesignerSatBitacoraPanel({ allClients = [] }) {
  const [bitacoraClientId, setBitacoraClientId] = useState("");
  const [busySatBitacora, setBusySatBitacora] = useState(false);

  const downloadSatBitacora = async () => {
    setBusySatBitacora(true);
    try {
      const params = { download: true, limit: 200, include_events: true };
      if (bitacoraClientId) params.client_id = bitacoraClientId;
      const { data, headers } = await api.get("/system/designer/sat-download-bitacora", {
        responseType: "blob",
        params,
        timeout: 180000,
      });
      if (!(data instanceof Blob) || data.size === 0) {
        toast.error("El servidor no devolvió un reporte válido");
        return;
      }
      const cdRaw = headers && (headers["content-disposition"] || headers["Content-Disposition"]);
      const cd = typeof cdRaw === "string" ? cdRaw : "";
      let filename = `sat-bitacora-${bitacoraClientId || "workspace"}-${new Date().toISOString().slice(0, 10)}.json`;
      const quoted = cd.match(/filename="([^"]+)"/i);
      if (quoted) filename = quoted[1];
      const blobUrl = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 2500);
      toast.success("Reporte de bitácora SAT descargado.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el reporte SAT");
    } finally {
      setBusySatBitacora(false);
    }
  };

  return (
    <div className="bg-violet-50 border border-violet-200 rounded-sm p-5 space-y-3">
      <h3 className="font-semibold text-violet-950 flex items-center gap-2 text-sm">
        <ServerCog className="w-4 h-4" />
        Reporte bitácora SAT (diseñador)
      </h3>
      <p className="text-[11px] text-violet-900/90 leading-snug">
        Exporta un JSON con llamadas SOAP al SAT, jobs en cola, intentos, fallas por cupo (5002/5005),
        continuaciones automáticas y flags del cliente. Solo disponible en el <strong>módulo Diseñador</strong>.
      </p>
      <div className="max-w-md space-y-1">
        <Label className="text-[10px] text-violet-900">Alcance del reporte</Label>
        <Select
          value={bitacoraClientId || "__workspace__"}
          onValueChange={(v) => setBitacoraClientId(v === "__workspace__" ? "" : v)}
        >
          <SelectTrigger className="h-9 text-xs bg-white">
            <SelectValue placeholder="Todo el espacio de trabajo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__workspace__">Todo el espacio (hasta 80 clientes)</SelectItem>
            {(allClients || []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {(c.rfc || "—")} — {c.razon_social || c.nombre || c.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        variant="outline"
        className="h-9 text-xs border-violet-300 text-violet-950 hover:bg-violet-100"
        disabled={busySatBitacora}
        onClick={downloadSatBitacora}
      >
        {busySatBitacora ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <FileDown className="w-4 h-4 mr-2" />
        )}
        Descargar reporte SAT (.json)
      </Button>
    </div>
  );
}

function LimpiezaRecuperacionTab() {
  const { user } = useAuth();
  const { mode } = useWorkspaceMode();
  const RESET_OPERATIONS_PHRASE = "LIMPIAR DATOS";
  const [resetOpPhrase, setResetOpPhrase] = useState("");
  const [busyResetOp, setBusyResetOp] = useState(false);
  const [busyBackup, setBusyBackup] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [busyRestore, setBusyRestore] = useState(false);
  const restoreInputRef = useRef(null);

  const canUse =
    Boolean(user?.is_admin_owner || (user?.is_buyer_tenant && user?.role === "buyer_admin")) &&
    mode !== "distribuidor";

  const runResetOperational = async () => {
    const normalized = resetOpPhrase.trim().toUpperCase().replace(/\s+/g, " ");
    if (normalized !== RESET_OPERATIONS_PHRASE) {
      toast.error(`Escriba exactamente: ${RESET_OPERATIONS_PHRASE}`);
      return;
    }
    if (
      !window.confirm(
        "¿Eliminar todas las empresas cliente, CFDI, cargas Excel/CSV, solicitudes SAT y datos de conciliación de este equipo? " +
          "No se borran usuarios, licencia ni el perfil fiscal guardado en registro inicial."
      )
    ) {
      return;
    }
    setBusyResetOp(true);
    try {
      const { data } = await api.post("/system/reset-operational-data", {
        confirm_phrase: resetOpPhrase.trim(),
      });
      const d = data?.deleted || {};
      toast.success(
        `Limpieza completada · Clientes: ${d.clients ?? 0} · CFDI: ${d.cfdis ?? 0} · Filas Excel: ${d.excel_rows ?? 0}`
      );
      setResetOpPhrase("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo completar la limpieza");
    } finally {
      setBusyResetOp(false);
    }
  };

  const downloadBackup = async () => {
    setBusyBackup(true);
    try {
      const { data, headers } = await api.get("/system/workspace-backup", {
        responseType: "blob",
        timeout: 600000,
      });
      if (!(data instanceof Blob)) {
        toast.error("Respuesta inválida del servidor");
        return;
      }
      if (data.size === 0) {
        toast.error("Archivo vacío");
        return;
      }
      const cdRaw = headers && (headers["content-disposition"] || headers["Content-Disposition"]);
      const cd = typeof cdRaw === "string" ? cdRaw : "";
      const isAttachment = cd.toLowerCase().includes("attachment");
      if (!isAttachment) {
        const t = await data.text();
        try {
          const j = JSON.parse(t);
          if (j.detail != null) {
            toast.error(formatApiError(j.detail) || "No se pudo generar el respaldo");
            return;
          }
        } catch {
          toast.error("Respuesta inesperada del servidor");
          return;
        }
        toast.error("Respuesta inesperada del servidor");
        return;
      }
      const head = (await data.slice(0, 1).text()).trim();
      if (head !== "{") {
        toast.error("El servidor no devolvió un archivo JSON de respaldo válido");
        return;
      }
      const blobUrl = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = blobUrl;
      const ts = new Date().toISOString().replace(/[-:T.Z]/g, "").slice(0, 12);
      a.download = `visor-fiscal-respaldo-${ts}.json`;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 2500);
      toast.success("Respaldo descargado — guárdelo en una carpeta segura de su equipo.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar el respaldo");
    } finally {
      setBusyBackup(false);
    }
  };

  const runRestore = async () => {
    if (!restoreFile) {
      toast.error("Seleccione un archivo de respaldo (.json)");
      return;
    }
    const extra = replaceExisting
      ? " Primero se eliminarán los datos operativos actuales de esta cuenta (igual que en «Limpieza») y luego se importará el archivo."
      : " Los documentos del archivo se escribirán en la base de datos (mismas claves sustituyen lo existente).";
    if (
      !window.confirm(
        "¿Restaurar desde el archivo seleccionado?" +
          extra +
          " Use un archivo que usted mismo exportó con esta misma cuenta."
      )
    ) {
      return;
    }
    setBusyRestore(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      fd.append("replace_existing", replaceExisting ? "true" : "false");
      const { data } = await api.post("/system/workspace-restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      const r = data?.restored || {};
      toast.success(
        `Restauración completada · Usuarios: ${r.users ?? 0} · Clientes: ${r.clients ?? 0} · CFDI: ${r.cfdis ?? 0} · Filas Excel: ${r.excel_rows ?? 0}`
      );
      setRestoreFile(null);
      if (restoreInputRef.current) restoreInputRef.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo restaurar el respaldo");
    } finally {
      setBusyRestore(false);
    }
  };

  if (!canUse) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          La <strong>Limpieza y recuperación de datos</strong> solo está disponible en modo <strong>Despacho</strong>, para el titular o
          el administrador del espacio comprador.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-3">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
          <FileDown className="w-4 h-4 text-[#0F2D5C]" />
          Respaldo a archivo (JSON)
        </h3>
        <p className="text-[11px] text-slate-600 leading-snug">
          Descarga un archivo de texto <strong>JSON</strong> a la carpeta que elija su navegador. Incluye empresas cliente, CFDI, cargas
          Excel/SAT/conciliación y <strong>usuarios</strong> del espacio. Las contraseñas van como <strong>hash</strong> (bcrypt), no en
          texto plano; el archivo sigue siendo sensible — guárdelo fuera del equipo o en lugar seguro. El respaldo <strong>no</strong> se
          guarda en la base de datos del programa.
        </p>
        <Button
          type="button"
          className="h-9 text-xs bg-[#0F2D5C] hover:bg-[#0a1f42]"
          disabled={busyBackup}
          onClick={downloadBackup}
        >
          {busyBackup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          Descargar respaldo
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
          <Upload className="w-4 h-4 text-[#0F2D5C]" />
          Recuperación desde respaldo
        </h3>
        <p className="text-[11px] text-slate-600 leading-snug">
          Elija el archivo <code className="text-[10px] bg-slate-100 px-1 rounded">.json</code> que exportó con este mismo usuario
          titular. Si activa <strong>«Sustituir datos actuales»</strong>, antes se ejecuta una limpieza operativa (como en la sección
          inferior) y luego se importa el contenido del archivo.
        </p>
        <div className="space-y-2">
          <Label className="text-[10px] text-slate-700">Archivo de respaldo</Label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <input
              ref={restoreInputRef}
              type="file"
              accept=".json,application/json"
              className="sr-only"
              onChange={(e) => setRestoreFile(e.target.files?.[0] ?? null)}
            />
            <Button
              type="button"
              variant="outline"
              className="h-9 text-xs w-fit shrink-0 border-slate-300"
              onClick={() => restoreInputRef.current?.click()}
              disabled={busyRestore}
            >
              Seleccionar archivo…
            </Button>
            <p
              className="text-[11px] text-slate-600 truncate min-w-0 flex-1"
              title={restoreFile?.name || undefined}
            >
              {restoreFile ? restoreFile.name : "Ningún archivo seleccionado"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="replace-existing-backup"
            checked={replaceExisting}
            onCheckedChange={(v) => setReplaceExisting(v === true)}
          />
          <Label htmlFor="replace-existing-backup" className="text-[11px] text-slate-700 cursor-pointer font-normal leading-snug">
            Sustituir datos actuales antes de importar (vaciar operativo y restaurar desde el archivo)
          </Label>
        </div>
        <Button
          type="button"
          variant="secondary"
          className="h-9 text-xs border border-slate-300"
          disabled={busyRestore || !restoreFile}
          onClick={runRestore}
        >
          {busyRestore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArchiveRestore className="w-4 h-4 mr-2" />}
          Restaurar desde archivo
        </Button>
      </div>

      <div className="rounded-sm border border-rose-300 bg-rose-50/80 p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Trash2 className="w-5 h-5 text-rose-800 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-bold text-rose-950">Limpieza de datos operativos</h4>
            <p className="text-[11px] text-rose-900/90 mt-1 leading-snug">
              Elimina <strong>empresas cliente</strong> (catálogo), <strong>CFDI</strong>, cargas <strong>Excel / CSV</strong>,{" "}
              <strong>solicitudes SAT</strong> y datos de <strong>conciliación / modo complemento</strong> asociados a su cuenta en
              este equipo.
            </p>
            <p className="text-[11px] text-rose-950 font-semibold mt-2">
              No borra: usuarios, licencia ni periodo de prueba, ni el perfil fiscal del titular. Para volver atrás después de una limpieza,
              use un <strong>respaldo JSON</strong> descargado previamente.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <Label className="text-[10px] text-rose-900">Escriba para confirmar</Label>
            <Input
              className="mt-1 h-9 text-xs font-mono"
              value={resetOpPhrase}
              onChange={(e) => setResetOpPhrase(e.target.value)}
              placeholder={RESET_OPERATIONS_PHRASE}
              autoComplete="off"
            />
          </div>
          <Button
            type="button"
            variant="destructive"
            className="h-9 text-xs shrink-0"
            disabled={busyResetOp}
            onClick={runResetOperational}
          >
            {busyResetOp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Ejecutar limpieza
          </Button>
        </div>
      </div>
    </div>
  );
}

function UsersAuxiliaresLegacyNotice() {
  return (
    <div
      className="rounded-sm border border-sky-200 bg-sky-50 px-4 py-3 text-xs text-sky-950 mb-4"
      data-testid="config-users-legacy-despacho-notice"
    >
      La administración operativa de usuarios auxiliares del comprador se realiza en{" "}
      <Link to="/?panel=equipo" className="font-semibold text-[#0F2D5C] underline">
        Panel General → Actividades de Equipo
      </Link>
      . Esta sección se conserva por compatibilidad/funciones legacy.
    </div>
  );
}

export default function Configuracion({ entryMode = null }) {
  const { user } = useAuth();
  const isEmisorHub = entryMode === "emisor";
  const isPlatformDesigner = usePlatformDesigner();
  const buyerPreviewActive = usePlatformDesignerBuyerPreview();
  const blockBuyerCommercialUi = blocksBuyerCommercialLicenseUi(user, {
    buyerPreviewActive,
    isPlatformDesigner,
  });
  const { mode, canSwitch } = useWorkspaceMode();
  const [searchParams] = useSearchParams();
  const showResellerUi = Boolean(
    !isPlatformDesigner && canSwitch && mode === "distribuidor" && user?.can_generate_licenses,
  );
  const isTrueOwner = !!user?.is_admin_owner;
  const isBuyerTenantAdmin = Boolean(user?.is_buyer_tenant && user?.role === "buyer_admin");
  const isAnyAdmin = isTrueOwner || user?.role === "admin" || isBuyerTenantAdmin || canManageWorkspaceTeam(user);
  const hideBuyerConfigAdminTabs = resolveHideBuyerConfigAdminTabs({
    user,
    workspaceMode: mode,
    canSwitch,
    buyerPreviewUx: buyerPreviewActive,
    blockBuyerCommercialUi,
    emisorView: isEmisorHub,
    canGenerateLicenses: Boolean(user?.can_generate_licenses),
  });
  const hideUsersTab = Boolean(
    (canSwitch && mode === "distribuidor") || hideBuyerConfigAdminTabs,
  );
  const hideBuyerTechnicalSatDiagnose = resolveHideBuyerTechnicalSatDiagnose({
    user,
    workspaceMode: mode,
    canSwitch,
    buyerPreviewUx: buyerPreviewActive,
    blockBuyerCommercialUi,
    entryMode,
    canGenerateLicenses: Boolean(user?.can_generate_licenses),
  });
  const canInstallLicense = canInstallLicenseForWorkspace(user);
  const showLimpiezaTab = Boolean(
    !hideBuyerConfigAdminTabs
    && (user?.is_admin_owner || (user?.is_buyer_tenant && user?.role === "buyer_admin"))
    && (isPlatformDesigner || mode === WORKSPACE_MODE_PLATFORM || mode !== "distribuidor")
  );

  /** Siempre cerrado al entrar de nuevo en Configuración (nuevo montaje de la ruta). */
  const [openSections, setOpenSections] = useState([]);

  useEffect(() => {
    if (isEmisorHub) return;
    setOpenSections((prev) => mergeLicenciaDeepLinkOpenSections(prev, searchParams));
  }, [searchParams, isEmisorHub]);

  // Lista de usuarios (solo administradores)
  const [users, setUsers] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [resetTarget, setResetTarget] = useState(null); // {id, email, name, role, permitted_clients}
  const [resetPwd, setResetPwd] = useState("");
  const [resetName, setResetName] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetRole, setResetRole] = useState("user");
  const [resetPermitted, setResetPermitted] = useState([]);
  const [busyReset, setBusyReset] = useState(false);
  const [editReseller, setEditReseller] = useState(() => ({ ...EMPTY_RESELLER }));
  const [newReseller, setNewReseller] = useState(() => ({ ...EMPTY_RESELLER }));

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } catch (e) {
      console.warn("[Configuracion] No se pudo cargar usuarios:", e?.response?.status);
      setUsers([]);
    }
  };

  const loadAllClients = async () => {
    try {
      const { data } = await api.get("/clients");
      setAllClients(data || []);
    } catch (e) {
      console.warn("[Configuracion] No se pudo cargar clientes:", e?.response?.status);
    }
  };

  useEffect(() => { 
    if (isAnyAdmin) {
      loadUsers();
      loadAllClients();
    }
  }, [isAnyAdmin]);

  const submitReset = async () => {
    const willChangePwd = !!resetPwd;
    const newName = (resetName || "").trim();
    const newEmail = (resetEmail || "").trim().toLowerCase();
    const willChangeName = newName && newName !== (resetTarget?.name || "");
    const willChangeEmail = newEmail && newEmail !== (resetTarget?.email || "");
    const willChangeRole = resetRole !== (resetTarget?.role || "user");
    const effectivePermitted = resetRole === "revendedor" ? [] : resetPermitted;
    const willChangePermitted =
      JSON.stringify([...effectivePermitted].sort()) !==
      JSON.stringify([...(resetTarget?.permitted_clients || [])].sort());
    const willChangeReseller =
      showResellerUi &&
      resetRole === "revendedor" &&
      JSON.stringify(editReseller) !== JSON.stringify(normalizeReseller(resetTarget?.reseller_profile));

    if (
      !willChangePwd &&
      !willChangeName &&
      !willChangeEmail &&
      !willChangeRole &&
      !willChangePermitted &&
      !willChangeReseller
    ) {
      toast.error("No hay cambios — modifica al menos un campo");
      return;
    }
    if (willChangePwd && resetPwd.length < 4) { toast.error("Contraseña con al menos 4 caracteres"); return; }
    if (willChangeEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) { toast.error("Correo electrónico inválido"); return; }
    
    setBusyReset(true);
    try {
      await api.post("/admin/users/reset", {
        user_id: resetTarget.id,
        new_password: willChangePwd ? resetPwd : null,
        new_name: willChangeName ? newName : null,
        new_email: willChangeEmail ? newEmail : null,
        new_role: willChangeRole ? resetRole : null,
        permitted_clients: willChangePermitted ? effectivePermitted : null,
        reseller_profile:
          showResellerUi && resetRole === "revendedor" && willChangeReseller ? editReseller : null,
      });
      toast.success(`Usuario ${newEmail || resetTarget.email} actualizado correctamente`);
      setResetTarget(null);
      setResetPwd(""); setResetName(""); setResetEmail("");
      loadUsers();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message || "Error desconocido";
      toast.error(msg);
    } finally { setBusyReset(false); }
  };

  useEffect(() => {
    if (resetTarget) {
      setResetName(resetTarget.name || "");
      setResetEmail(resetTarget.email || "");
      setResetRole(resetTarget.role || "user");
      setResetPermitted(resetTarget.permitted_clients || []);
      setEditReseller(normalizeReseller(resetTarget.reseller_profile));
      setResetPwd("");
    }
  }, [resetTarget]);

  const toggleAdminOwner = async (u) => {
    const enable = !u.is_admin_owner;
    if (enable && !window.confirm(`¿Marcar a ${u.email} como administrador propietario?`)) return;
    try {
      await api.post(`/admin/users/toggle-admin-owner?user_id=${u.id}&enabled=${enable}`);
      toast.success(`${u.email} actualizado`);
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`¿Eliminar la cuenta de ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Cuenta ${u.email} eliminada`);
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    }
  };

  const [suspendingId, setSuspendingId] = useState(null);
  const suspendUser = async (u) => {
    if (!window.confirm(`¿Suspender la cuenta de ${u.email}? Conservará historial pero no podrá operar.`)) return;
    setSuspendingId(u.id);
    try {
      await api.post(`/admin/users/suspend?user_id=${encodeURIComponent(u.id)}`);
      toast.success(`Cuenta ${u.email} suspendida`);
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al suspender");
    } finally {
      setSuspendingId(null);
    }
  };

  // Crear usuario
  const [createOpen, setCreateOpen] = useState(false);
  const [newU, setNewU] = useState({ email: "", name: "", role: "user", password: "", permitted_clients: [] });
  const [busyCreate, setBusyCreate] = useState(false);
  const submitCreate = async () => {
    if (!newU.email || !newU.name) { toast.error("Correo y nombre son obligatorios"); return; }
    if (!newU.password) { toast.error("Define una contraseña inicial"); return; }
    if (newU.password.length < 4) { toast.error("La contraseña debe tener al menos 4 caracteres"); return; }
    setBusyCreate(true);
    try {
      await api.post("/admin/users/create", {
        email: newU.email.trim().toLowerCase(),
        name: newU.name.trim(),
        role: isBuyerTenantAdmin ? "user" : newU.role,
        password: newU.password,
        permitted_clients: newU.role === "revendedor" ? [] : newU.permitted_clients,
        reseller_profile:
          showResellerUi && newU.role === "revendedor" ? newReseller : undefined,
      });
      toast.success(`Usuario ${newU.email} creado correctamente`);
      setCreateOpen(false);
      setNewU({ email: "", name: "", role: "user", password: "", permitted_clients: [] });
      setNewReseller({ ...EMPTY_RESELLER });
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al crear");
    } finally { setBusyCreate(false); }
  };

  const configPageTitle =
    entryMode === "despacho"
      ? "Configuración del despacho"
      : isEmisorHub
        ? "Gestor de licencias distribuidor"
        : hideUsersTab
          ? "Gestor de licencias"
          : "Configuración";
  const configPageSubtitle =
    entryMode === "despacho"
      ? hideBuyerConfigAdminTabs
        ? "Mi Negocio, licencia, datos del titular y solicitudes comerciales."
        : "Perfil del titular, usuarios del equipo, licencia y limpieza del espacio de trabajo."
      : isEmisorHub
        ? "Emisión e importación de licencias para compradores del modelo distribuidor. Las herramientas técnicas, usuarios y datos del negocio se administran en sus módulos correspondientes."
        : hideUsersTab
          ? "Emisión y seguimiento de licencias, datos del titular y recuperación de acceso."
          : "Gestiona tu cuenta, recuperación de acceso y usuarios del sistema.";
  const configBreadcrumbLast =
    entryMode === "despacho"
      ? "Configuración del despacho"
      : isEmisorHub
        ? "Gestor de licencias distribuidor"
        : hideUsersTab
          ? "Gestor de licencias"
          : "Configuración";
  const { canEditDesignerAccess, config: designerAccess, reload: reloadDesignerAccess } =
    useDesignerUiAccess(user);
  const showDesignerSatBitacora =
    getStoredWorkModule() === WORK_MODULES.DISENADOR && isPlatformDesigner;

  const licenseCommercialFocus = shouldUseLicenseCommercialFocusView({
    searchParams,
    user,
    workspaceMode: mode,
    canSwitch,
    isPlatformDesigner,
    buyerPreviewActive,
    isEmisorHub,
  });

  if (licenseCommercialFocus) {
    return <LicenseCommercialFocusView />;
  }

  return (
    <>
      <PageHeader
        title={configPageTitle}
        subtitle={configPageSubtitle}
        breadcrumb={[VISOR_MODULE_BREADCRUMB_ROOT, configBreadcrumbLast]}
      />
      <div className="p-8 space-y-6 max-w-5xl" data-testid={isEmisorHub ? "config-page-emisor" : "config-page-full"}>
        {isEmisorHub ? (
          <EmisorScopeNotice />
        ) : entryMode && !hideBuyerConfigAdminTabs ? (
          <p
            className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-slate-800 m-0"
            data-testid={`config-entry-notice-${entryMode}`}
            role="note"
          >
            <strong>Configuración del despacho</strong> — ruta canónica para titular y comprador.
            El alias <code className="text-xs">/configuracion</code> redirige aquí en modo Despacho.
          </p>
        ) : null}
        {!isEmisorHub && !hideBuyerTechnicalSatDiagnose ? <SatUiDiagnoseSettings /> : null}
        {isEmisorHub ? (
          <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-4 md:p-5">
            <SystemTab
              key={mode}
              emisorView
              isOwner={isTrueOwner || isBuyerTenantAdmin}
              canInstallLicense={canInstallLicense}
              showSolicitudLicenciaBanner={searchParams.get("solicitar_licencia") === "1"}
              showLicenseSigningHints={Boolean(user?.can_generate_licenses)}
              showActivarLicenciaScroll={searchParams.get("activar_licencia") === "1"}
              showBuyerPanelHint={Boolean(canInstallLicense && user?.is_buyer_tenant)}
            />
          </div>
        ) : hideBuyerConfigAdminTabs ? (
          <div
            className="rounded-sm border border-slate-200 bg-white shadow-sm p-4 md:p-5"
            data-testid="config-buyer-clean-shell"
          >
            <SystemTab
              key={mode}
              isOwner={isTrueOwner || isBuyerTenantAdmin}
              canInstallLicense={canInstallLicense}
              showSolicitudLicenciaBanner={searchParams.get("solicitar_licencia") === "1"}
              showLicenseSigningHints={Boolean(user?.can_generate_licenses)}
              showActivarLicenciaScroll={searchParams.get("activar_licencia") === "1"}
              showBuyerPanelHint={Boolean(canInstallLicense && user?.is_buyer_tenant)}
            />
          </div>
        ) : (
        <Accordion type="multiple" value={openSections} onValueChange={setOpenSections} className="rounded-sm border border-slate-200 bg-white shadow-sm">
          {!hideUsersTab ? (
            <AccordionItem value="usuarios" className="border-slate-200 px-0">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <UserCog className="w-4 h-4 shrink-0" />
                  Usuarios
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
                <div className="pt-4">
                  {canEditDesignerAccess ? (
                    <Tabs defaultValue="equipo" className="w-full" data-testid="config-users-tabs">
                      <TabsList className="grid w-full max-w-lg grid-cols-2 h-9 rounded-sm mb-4 bg-slate-100 p-1">
                        <TabsTrigger
                          value="equipo"
                          className="text-xs rounded-sm"
                          data-testid="config-users-tab-equipo"
                        >
                          Usuarios del equipo
                        </TabsTrigger>
                        <TabsTrigger
                          value="disenador"
                          className="text-xs rounded-sm"
                          data-testid="config-users-tab-disenador"
                        >
                          Diseñador
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="equipo" className="mt-0">
                        {isAnyAdmin ? (
                          <div className="bg-white border border-slate-200 rounded-sm p-5">
                            <div className="flex items-start justify-between mb-3">
                              <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm" style={{fontFamily:"'Chivo'"}}>
                                <UserCog className="w-4 h-4"/> Gestión de usuarios
                              </h3>
                              <Button onClick={()=>setCreateOpen(true)} className="h-9 rounded-sm bg-[#3DA873] hover:bg-[#3DA873]/90 text-xs">
                                <UserPlus className="w-3.5 h-3.5 mr-1.5"/> Dar de alta usuario
                              </Button>
                            </div>
                            <p className="text-xs text-slate-500 mb-4">
                              Como administrador puedes gestionar los roles y permisos de acceso a empresas de cada usuario de tu proyecto.
                              {isBuyerTenantAdmin ? (
                                <>
                                  {" "}
                                  <span className="font-semibold text-[#0F2D5C]">Cuenta de comprador:</span> solo puedes dar de alta usuarios auxiliares para tu despacho.
                                </>
                              ) : null}
                              {showResellerUi ? (
                                <>
                                  {" "}
                                  En <span className="font-semibold text-violet-800">modo Distribuidor</span> puedes dar de alta el rol{" "}
                                  <span className="font-semibold">Revendedor</span> (sin cartera fiscal CFDI) con datos de facturación opcionales.
                                </>
                              ) : null}
                            </p>
                            {!showResellerUi ? <UsersAuxiliaresLegacyNotice /> : null}
                            <UserManagementGroupedTables
                              users={users}
                              showResellerUi={showResellerUi}
                              currentUserId={user?.id}
                              onEdit={setResetTarget}
                              onDelete={deleteUser}
                              onSuspend={suspendUser}
                              suspendingId={suspendingId}
                            />
                          </div>
                        ) : (
                          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5"/>
                            <div>La gestión de usuarios solo está disponible para administradores.</div>
                          </div>
                        )}
                      </TabsContent>
                      <TabsContent value="disenador" className="mt-0 space-y-4">
                        {isPlatformDesigner ? <PlatformLicenseRequests embedded /> : null}
                        <BuyerRequestsDesignerPanel
                          users={users}
                          showAccountTrackingTitle={isPlatformDesigner}
                        />
                        <DesignerUiAccessConfig
                          initialEmail={designerAccess?.authorizedEmail}
                          onSaved={() => reloadDesignerAccess()}
                        />
                      </TabsContent>
                    </Tabs>
                  ) : isAnyAdmin ? (
                    <div className="bg-white border border-slate-200 rounded-sm p-5">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm" style={{fontFamily:"'Chivo'"}}>
                          <UserCog className="w-4 h-4"/> Gestión de usuarios
                        </h3>
                        <Button onClick={()=>setCreateOpen(true)} className="h-9 rounded-sm bg-[#3DA873] hover:bg-[#3DA873]/90 text-xs">
                          <UserPlus className="w-3.5 h-3.5 mr-1.5"/> Dar de alta usuario
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mb-4">
                        Como administrador puedes gestionar los roles y permisos de acceso a empresas de cada usuario de tu proyecto.
                        {isBuyerTenantAdmin ? (
                          <>
                            {" "}
                            <span className="font-semibold text-[#0F2D5C]">Cuenta de comprador:</span> solo puedes dar de alta usuarios auxiliares para tu despacho.
                          </>
                        ) : null}
                        {showResellerUi ? (
                          <>
                            {" "}
                            En <span className="font-semibold text-violet-800">modo Distribuidor</span> puedes dar de alta el rol{" "}
                            <span className="font-semibold">Revendedor</span> (sin cartera fiscal CFDI) con datos de facturación opcionales.
                          </>
                        ) : null}
                      </p>
                      {!showResellerUi ? <UsersAuxiliaresLegacyNotice /> : null}
                      <UserManagementGroupedTables
                        users={users}
                        showResellerUi={showResellerUi}
                        currentUserId={user?.id}
                        onEdit={setResetTarget}
                        onDelete={deleteUser}
                        onSuspend={suspendUser}
                        suspendingId={suspendingId}
                      />
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5"/>
                      <div>La gestión de usuarios solo está disponible para administradores.</div>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ) : null}

          {showDesignerSatBitacora ? (
            <AccordionItem value="designer-sat-bitacora" className="border-slate-200 px-0">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <ServerCog className="w-4 h-4 shrink-0" />
                  Diagnóstico SAT (diseñador)
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
                <div className="pt-4">
                  <DesignerSatBitacoraPanel allClients={allClients} />
                </div>
              </AccordionContent>
            </AccordionItem>
          ) : null}

          {showLimpiezaTab ? (
            <AccordionItem value="limpieza" className="border-slate-200 px-0">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <ArchiveRestore className="w-4 h-4 shrink-0" />
                  Limpieza y recuperación
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
                <div className="pt-4">
                  <LimpiezaRecuperacionTab />
                </div>
              </AccordionContent>
            </AccordionItem>
          ) : null}

          <AccordionItem value="sistema" className="border-slate-200 px-0 border-b-0">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <ServerCog className="w-4 h-4 shrink-0" />
                Mi Negocio / Licencia
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4">
                <SystemTab
                  key={mode}
                  isOwner={isTrueOwner || isBuyerTenantAdmin}
                  canInstallLicense={canInstallLicense}
                  showSolicitudLicenciaBanner={searchParams.get("solicitar_licencia") === "1"}
                  showLicenseSigningHints={Boolean(user?.can_generate_licenses)}
                  showActivarLicenciaScroll={searchParams.get("activar_licencia") === "1"}
                  showBuyerPanelHint={Boolean(canInstallLicense && user?.is_buyer_tenant)}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        )}
      </div>

      {/* Modal: editar usuario */}
      <Dialog open={!!resetTarget} onOpenChange={(v)=>!v && setResetTarget(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4 text-amber-600"/> Editar usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#0F2D5C] text-white rounded-sm p-3 flex items-start gap-3">
              <UserCog className="w-6 h-6 text-[#3DA873] mt-0.5 flex-shrink-0"/>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold">Editando a</div>
                <div className="font-mono text-sm font-bold mt-0.5 truncate">{resetTarget?.email}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-xs">Nombre del usuario</Label>
                <Input value={resetName} onChange={(e)=>setResetName(e.target.value)} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs">Correo electrónico</Label>
                <Input type="email" value={resetEmail} onChange={(e)=>setResetEmail(e.target.value)} className="mt-1 rounded-sm font-mono" disabled={resetTarget?.is_buyer_tenant} />
              </div>
              <UserRoleField
                targetUser={resetTarget}
                value={resetRole}
                onChange={setResetRole}
                showReseller={showResellerUi}
                isBuyerTenantAdmin={isBuyerTenantAdmin}
              />
              {isTrueOwner && !isBuyerTenantAdmin && !resetTarget?.is_admin_owner && resetRole !== "revendedor" && (
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox 
                    id="make-owner" 
                    checked={resetRole === "admin"} 
                    onCheckedChange={(checked) => setResetRole(checked ? "admin" : "user")}
                  />
                  <Label htmlFor="make-owner" className="text-xs text-slate-600 cursor-pointer">
                    Marcar como Administrador
                  </Label>
                </div>
              )}
            </div>

            {resetRole === "user" && (
              <div className="border-t border-slate-100 pt-3">
                <Label className="text-xs font-semibold text-slate-700 mb-2 block">Empresas permitidas</Label>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2 space-y-2">
                  {allClients.map(c => (
                    <div key={c.id} className="flex items-center gap-2">
                      <Checkbox 
                        id={`edit-perm-${c.id}`} 
                        checked={resetPermitted.includes(c.id)}
                        onCheckedChange={(checked) => {
                          if (checked) setResetPermitted([...resetPermitted, c.id]);
                          else setResetPermitted(resetPermitted.filter(id => id !== c.id));
                        }}
                      />
                      <Label htmlFor={`edit-perm-${c.id}`} className="text-[11px] text-slate-600 cursor-pointer truncate">{c.razon_social}</Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showResellerUi && resetRole === "revendedor" && (
              <ResellerProfileFields value={editReseller} onChange={setEditReseller} idPrefix="edit" />
            )}

            <div className="border-t border-slate-100 pt-3">
              <Label className="text-xs">Nueva contraseña (opcional)</Label>
              <PasswordInput value={resetPwd} onChange={(e)=>setResetPwd(e.target.value)} className="mt-1 rounded-sm font-mono" placeholder="mínimo 4 caracteres" autoComplete="new-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>{ setResetTarget(null); setResetPwd(""); }} className="rounded-sm">Cancelar</Button>
            <Button onClick={submitReset} disabled={busyReset} className="rounded-sm bg-amber-500 hover:bg-amber-600 text-white">
              {busyReset && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>} Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: crear usuario */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#3DA873]"/> Nuevo usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Correo electrónico</Label>
              <Input type="email" value={newU.email} onChange={(e)=>setNewU(s=>({...s, email: e.target.value}))} className="mt-1 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs">Nombre completo</Label>
              <Input value={newU.name} onChange={(e)=>setNewU(s=>({...s, name: e.target.value}))} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs">Rol</Label>
              {!isBuyerTenantAdmin ? (
              <Select
                value={newU.role}
                onValueChange={(v) =>
                  setNewU((s) => ({
                    ...s,
                    role: v,
                    permitted_clients: v === "revendedor" || v === "consulta" ? [] : s.permitted_clients,
                  }))
                }
              >
                <SelectTrigger className="mt-1 rounded-sm h-9 text-xs"><SelectValue placeholder="Seleccione un rol" /></SelectTrigger>
                <SelectContent>
                  {getAssignableWorkspaceRoleOptions({ showReseller: showResellerUi }).map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              ) : (
                <p className="mt-2 text-xs text-slate-600 rounded-sm border border-slate-100 bg-slate-50 px-3 py-2">
                  Solo puedes dar de alta <strong>auxiliares contables</strong> para tu despacho.
                </p>
              )}
            </div>

            {(isBuyerTenantAdmin || newU.role === "user") && (
              <div className="border-t border-slate-100 pt-3">
                <Label className="text-xs font-semibold text-slate-700 mb-2 block">Empresas permitidas</Label>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2 space-y-2">
                  {allClients.map(c => (
                    <div key={c.id} className="flex items-center gap-2">
                      <Checkbox 
                        id={`new-perm-${c.id}`} 
                        checked={(newU.permitted_clients || []).includes(c.id)}
                        onCheckedChange={(checked) => {
                          const current = newU.permitted_clients || [];
                          if (checked) setNewU({...newU, permitted_clients: [...current, c.id]});
                          else setNewU({...newU, permitted_clients: current.filter(id => id !== c.id)});
                        }}
                      />
                      <Label htmlFor={`new-perm-${c.id}`} className="text-[11px] text-slate-600 cursor-pointer truncate">{c.razon_social}</Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showResellerUi && newU.role === "revendedor" && (
              <ResellerProfileFields value={newReseller} onChange={setNewReseller} idPrefix="new" />
            )}

            <div className="border-t border-slate-100 pt-3">
              <Label className="text-xs">Contraseña inicial</Label>
              <PasswordInput value={newU.password} onChange={(e)=>setNewU(s=>({...s, password: e.target.value}))} className="mt-1 rounded-sm font-mono" placeholder="mín. 4" autoComplete="new-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreateOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submitCreate} disabled={busyCreate} className="rounded-sm bg-[#3DA873] hover:bg-[#3DA873]/90">
              {busyCreate && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>} Crear usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}
